import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { auth,db } from "../../firebase/config";
import {
  collection,
  onSnapshot,
  doc,
  getDoc,
  updateDoc,
  addDoc,
  query,
  where,
  getDocs,
  serverTimestamp,
  writeBatch,
  runTransaction,
  increment,
} from "firebase/firestore";
import { useLocation, useNavigate } from "react-router-dom";
import Chat from "../Messaging/Chat";
import ReportButton from "../Common/ReportButton";
import { AuthActionGate, useAuthActionGate } from "../Common/AuthActionGate";
import { getDistanceMiles } from "../../utils/distance";
import {
  getLocationDiagnostics,
  getSafeDistanceMiles,
  isBroadAreaMatch,
  normalizeLocationRecord,
  productPassesMarketplaceRadius,
  selectViewerLocation,
} from "../../utils/locationUtils";
import { getFarmIconMeta } from "../../utils/farmIcons";
import { hasPremiumAccess } from "../../utils/access";
import { requireVerifiedEmail } from "../../utils/emailVerification";
import {
  MARKETPLACE_SECTIONS,
  PLANT_CATEGORY_OPTIONS,
  getPlantCategoryLabel,
  getPlantLifeCycleLabel,
  getSunNeedsLabel,
} from "../../constants/plantsGarden";
import {
  PRODUCT_AVAILABILITY,
  PRODUCT_AVAILABILITY_FILTERS,
  getEventRegistrationLabel,
  getProductAvailabilityLabel,
  getProductAvailabilityStatus,
} from "../../constants/availability";
import {
  formatAvailability,
  formatProductPrice,
  isStartsAtPrice,
} from "../../utils/productDisplay";
import {
  getMarketplaceProductDisplayLocationLabel,
  getMarketplaceProductLinkedStand,
} from "../../utils/marketplaceListings";
import {
  formatEventDateRange,
  formatEventTimeRange,
  getEventLocationText,
  shouldShowEventRegistrationStatus,
} from "../../utils/eventDisplay";
import {
  EXCHANGE_TYPES,
  getBarterCategoryLabel,
  getExchangeBadges,
  getTradeMessageDraft,
  normalizeBarterFields,
} from "../../utils/barter";
import { getProfilePath } from "../../utils/storefront";
import { getPublicEventAttribution } from "../../utils/eventRadar";
import {
  completeTrackedFlow,
  startTrackedFlow,
  trackUsageEvent,
} from "../Analytics/CampaignTrafficTracker";

const HOME_WEATHER_FALLBACK_LOCATION = {
  lat: 35.4834,
  lng: -86.4603,
};

const MARKET_PRODUCT_CATEGORY_FILTERS = [
  { value: "", label: "All" },
  { value: "produce", label: "Produce" },
  { value: "eggs", label: "Eggs" },
  { value: "meat", label: "Meat" },
  { value: "dairy", label: "Dairy" },
  { value: "handmade", label: "Handmade" },
  { value: "plants", label: "Plants & Garden" },
  { value: "hay_straw", label: "Hay / Feed" },
  { value: "supplies_services", label: "Equipment / Tools" },
  { value: "services", label: "Services" },
  { value: "other", label: "Other" },
];

const getHomeWeatherCodeLabel = (code) => {
  const weatherCode = Number(code);
  if (weatherCode === 0) return "Clear";
  if (weatherCode === 1) return "Mainly clear";
  if (weatherCode === 2) return "Partly cloudy";
  if (weatherCode === 3) return "Overcast";
  if ([45, 48].includes(weatherCode)) return "Fog";
  if ([51, 53, 55].includes(weatherCode)) return "Drizzle";
  if ([61, 63, 65].includes(weatherCode)) return "Rain";
  if ([66, 67].includes(weatherCode)) return "Freezing rain";
  if ([71, 73, 75].includes(weatherCode)) return "Snow";
  if ([80, 81, 82].includes(weatherCode)) return "Rain showers";
  if (weatherCode === 95) return "Thunderstorms";
  if ([96, 99].includes(weatherCode)) return "Thunderstorms with hail";
  return "Weather update";
};

const getHomeMoonPhase = (date) => {
  const knownNewMoon = new Date(Date.UTC(2000, 0, 6, 18, 14)).getTime();
  const lunarCycle = 29.53058867;
  const daysSince = (date.getTime() - knownNewMoon) / 86400000;
  const moonAge = ((daysSince % lunarCycle) + lunarCycle) % lunarCycle;

  if (moonAge < 1.85) return "New moon";
  if (moonAge < 5.54) return "Waxing crescent";
  if (moonAge < 9.23) return "First quarter";
  if (moonAge < 12.92) return "Waxing gibbous";
  if (moonAge < 16.61) return "Full moon";
  if (moonAge < 20.3) return "Waning gibbous";
  if (moonAge < 23.99) return "Last quarter";
  return "Waning crescent";
};

const getHomeFrostRisk = (month, currentTemp, dailyLow) => {
  if (Number.isFinite(currentTemp) && currentTemp <= 32) return "High";
  if (Number.isFinite(dailyLow) && dailyLow <= 32) return "High";
  if (Number.isFinite(currentTemp) && currentTemp <= 38) return "Watch";
  if (Number.isFinite(dailyLow) && dailyLow <= 38) return "Watch";
  if ([11, 0, 1].includes(month)) return "High";
  if ([2, 3, 9, 10].includes(month)) return "Watch";
  return "Low";
};

const getHomeFieldCondition = (dailyPrecip, currentPrecip, currentRain) => {
  const precip =
    (Number.isFinite(Number(dailyPrecip)) ? Number(dailyPrecip) : 0) +
    (Number.isFinite(Number(currentPrecip)) ? Number(currentPrecip) : 0) +
    (Number.isFinite(Number(currentRain)) ? Number(currentRain) : 0);

  if (precip >= 0.35) return "Wet/Muddy";
  if (precip >= 0.05) return "Damp";
  return "Dry";
};

const getHomeWeatherLocation = (userLocation, userProfile) => {
  const profileLocation = userProfile?.location || {};
  const options = [
    { lat: userLocation?.lat, lng: userLocation?.lng },
    { lat: profileLocation.lat, lng: profileLocation.lng },
    { lat: userProfile?.lat, lng: userProfile?.lng },
  ];
  const usable = options.find(
    (item) => Number.isFinite(Number(item.lat)) && Number.isFinite(Number(item.lng))
  );

  return {
    lat: usable ? Number(usable.lat) : HOME_WEATHER_FALLBACK_LOCATION.lat,
    lng: usable ? Number(usable.lng) : HOME_WEATHER_FALLBACK_LOCATION.lng,
  };
};

const buildHomeWeatherUrl = ({ lat, lng }) => {
  const params = new URLSearchParams({
    latitude: String(lat),
    longitude: String(lng),
    current:
      "temperature_2m,apparent_temperature,precipitation,rain,weather_code,wind_speed_10m,wind_gusts_10m",
    daily: "temperature_2m_min,precipitation_sum,precipitation_probability_max",
    temperature_unit: "fahrenheit",
    wind_speed_unit: "mph",
    precipitation_unit: "inch",
    forecast_days: "1",
    timezone: "auto",
  });

  return `https://api.open-meteo.com/v1/forecast?${params.toString()}`;
};


  const JobFeed = ({
    currentUser,
    userLocation,
    userProfile,
    productSection = MARKETPLACE_SECTIONS.GENERAL,
    initialDashboardView = "worker",
  }) => {
  const premiumAllowed = hasPremiumAccess(userProfile);
  const [fields, setFields] = useState([]);
  const [dayJobs, setDayJobs] = useState([]);
  const [products, setProducts] = useState([]);
  const [marketplaceFarmStands, setMarketplaceFarmStands] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [usersMap, setUsersMap] = useState({});
  const [filterState, setFilterState] = useState("");
  const [radius, setRadius] = useState(25);
  const [applicationsMap, setApplicationsMap] = useState({});
  const [bidsMap, setBidsMap] = useState({});
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [dashboardView, setDashboardView] = useState(initialDashboardView);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productCategoryFilter, setProductCategoryFilter] = useState("");
  const [marketProductTypeFilter, setMarketProductTypeFilter] = useState("");
  const [productAvailabilityFilter, setProductAvailabilityFilter] = useState("active_future");
  const [productExchangeFilter, setProductExchangeFilter] = useState("all");
  const [productSort, setProductSort] = useState("nearest");
  const [productReviews, setProductReviews] = useState([]);
  const [reviewTarget, setReviewTarget] = useState(null);
const [reviewRating, setReviewRating] = useState(5);
const [reviewComment, setReviewComment] = useState("");
const [submittingReview, setSubmittingReview] = useState(false);
const [reviewsMap, setReviewsMap] = useState({});
  const [requestQuantity, setRequestQuantity] = useState(1);
  const [openedFocusProductId, setOpenedFocusProductId] = useState(null);
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventReservations, setEventReservations] = useState([]);
  const [reservationEvent, setReservationEvent] = useState(null);
const [reservationCategoryId, setReservationCategoryId] = useState("");
const [reservationNote, setReservationNote] = useState("");
const [submittingReservation, setSubmittingReservation] = useState(false);
const [reservationForm, setReservationForm] = useState({
  boothType: "Vendor",
  note: "",
});
const [homeWeatherState, setHomeWeatherState] = useState({
  data: null,
  error: false,
  loading: true,
});
const [chatInitialMessage, setChatInitialMessage] = useState("");

  const location = useLocation();
  const navigate = useNavigate();
  const { requireSignIn, gateProps: authGateProps } = useAuthActionGate(currentUser);
  const isPlantsGardenPage = productSection === MARKETPLACE_SECTIONS.PLANTS_GARDEN;
  const marketDebugEnabled =
    typeof window !== "undefined" &&
    ["localhost", "127.0.0.1"].includes(window.location.hostname) &&
    new URLSearchParams(location.search || "").get("marketDebug") === "1";
  useEffect(() => {
    if (!location.pathname.startsWith("/jobs")) return;
    trackUsageEvent("job_viewed", {
      feature: "jobs",
      action: dashboardView || "view",
      routeName: "Jobs",
    });
  }, [dashboardView, location.pathname]);

  const requirePublicJobAction = (eventType, message, relatedId = "") => {
    if (!currentUser?.uid) {
      trackUsageEvent(eventType, { feature: "jobs", action: "intent", relatedId });
      trackUsageEvent("auth_gate_open", { feature: "jobs", action: eventType, relatedId });
    }
    return requireSignIn(message);
  };

  const trackPublicOpen = (eventType, feature, relatedId = "") => {
    if (!currentUser?.uid) {
      trackUsageEvent(eventType, { feature, action: "open", relatedId });
    }
  };
  const marketViewerLocation = useMemo(
    () =>
      selectViewerLocation({
        savedProfile: userProfile,
        browserLocation: userLocation,
        allowBrowserLocation: true,
        allowFallback: false,
      }),
    [userProfile, userLocation]
  );

const requirePremiumAction = (message) => {
  if (!requireSignIn("Create a free account or sign in to use this member feature.")) {
    return false;
  }

  if (premiumAllowed) return true;

  alert(
    message ||
      "Premium is required for this action. You can still browse and buy local products for free."
  );

  navigate("/subscribe");
  return false;
};

const isProductOwnedByCurrentUser = (product) => {
  if (!currentUser?.uid || !product) return false;

  return [product.userId, product.ownerId, product.sellerId].includes(currentUser.uid);
};

const archiveOwnPlantListing = async (product) => {
  if (!isProductOwnedByCurrentUser(product)) {
    alert("You can only delete your own listing.");
    return;
  }

  const confirmed = window.confirm(
    "Delete this Plants & Garden listing? It will be archived and will no longer appear publicly. Existing messages, requests, reviews, and image links will be preserved."
  );

  if (!confirmed) return;

  try {
    await updateDoc(doc(db, "products", product.id), {
      isArchived: true,
      status: "archived",
      availabilityStatus: PRODUCT_AVAILABILITY.ARCHIVED,
      archivedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    setSelectedProduct(null);
  } catch (error) {
    console.error("Unable to archive plant listing:", error);
    alert("Could not delete this listing. Please try again.");
  }
};

useEffect(() => {
  if (!selectedProduct || typeof document === "undefined") return undefined;

  const previousOverflow = document.body.style.overflow;
  const previousPaddingRight = document.body.style.paddingRight;
  const scrollbarWidth =
    window.innerWidth - document.documentElement.clientWidth;

  document.body.style.overflow = "hidden";

  if (scrollbarWidth > 0) {
    document.body.style.paddingRight = `${scrollbarWidth}px`;
  }

  return () => {
    document.body.style.overflow = previousOverflow;
    document.body.style.paddingRight = previousPaddingRight;
  };
}, [selectedProduct]);

  const focusJobId = location.state?.focusJobId || location.state?.focusItemId;
  const requestedDashboardView = location.state?.dashboardView;
  const requestedRadius = location.state?.radius;

  const homeWeatherLocation = useMemo(
    () => getHomeWeatherLocation(userLocation, userProfile),
    [userLocation, userProfile]
  );

  useEffect(() => {
    const controller = new AbortController();

    const loadHomeWeather = async () => {
      setHomeWeatherState((current) => ({ ...current, loading: true, error: false }));

      try {
        const response = await fetch(buildHomeWeatherUrl(homeWeatherLocation), {
          signal: controller.signal,
        });

        if (!response.ok) throw new Error("Weather unavailable");

        const data = await response.json();
        setHomeWeatherState({ data, error: false, loading: false });
      } catch (error) {
        if (error.name === "AbortError") return;
        setHomeWeatherState((current) => ({ ...current, error: true, loading: false }));
      }
    };

    loadHomeWeather();

    return () => controller.abort();
  }, [homeWeatherLocation.lat, homeWeatherLocation.lng]);

// 📅 Calendar helper
const getDaysInMonth = (date) => {
  const year = date.getFullYear();
  const month = date.getMonth();

  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);

  const days = [];

  // Fill empty cells before month starts
  for (let i = 0; i < firstDay.getDay(); i++) {
    days.push(null);
  }

  // Actual days
  for (let d = 1; d <= lastDay.getDate(); d++) {
    days.push(new Date(year, month, d));
  }

  return days;
};

  const focusProductId = location.state?.focusProductId;
 const [currentMonth, setCurrentMonth] = useState(new Date());
  const formatLocation = (city, state) => {
  if (city && state) return `${city}, ${state}`;
  if (city) return city;
  if (state) return state;
  return "Location not set";
};

const normalizeSearch = (value) =>
  String(value || "").trim().toLowerCase();

const getFieldJobDisplayLocation = (job) => {
  const firstField = Array.isArray(job.fields) ? job.fields[0] : null;

  const city = job.city || firstField?.city || "";
  const county = job.county || firstField?.county || "";
  const state = job.state || firstField?.state || "";
  const locationText = job.locationText || firstField?.locationText || "";

  const cityState = [city, state].filter(Boolean).join(", ");

  return cityState || county || locationText || "Location not listed";
};

const getJobLocationSearchText = (job) => {
  const primaryLocation = getJobPrimaryLocation(job);
  const firstField = Array.isArray(job.fields) ? job.fields[0] : null;

  return normalizeSearch(
    [
      primaryLocation.city,
      primaryLocation.state,
      job.city,
      job.county,
      job.state,
      job.locationText,
      firstField?.city,
      firstField?.county,
      firstField?.state,
      firstField?.locationText,
    ]
      .filter(Boolean)
      .join(" ")
  );
};

const getReviewsForProduct = (productId) => {
  return productReviews.filter((review) => review.productId === productId);
};

const getProductReviewStats = (productId) => {
  const reviews = getReviewsForProduct(productId);

  if (reviews.length === 0) {
    return {
      count: 0,
      average: 0,
    };
  }

  const total = reviews.reduce(
    (sum, review) => sum + Number(review.rating || 0),
    0
  );

  return {
    count: reviews.length,
    average: Number((total / reviews.length).toFixed(1)),
  };
};

const getProductRequestButtonLabel = (product) => {
  const status = getProductAvailabilityStatus(product);
  if (status === PRODUCT_AVAILABILITY.COMING_SOON) return "Not Available Yet";
  if (
    status === PRODUCT_AVAILABILITY.OUT_OF_STOCK ||
    product.status === "out_of_stock" ||
    Number(product.quantityAvailable ?? product.quantity ?? 1) <= 0
  ) {
    return "Out of Stock";
  }
  return "Request to Buy";
};

const getMarketProductCategoryValue = (product = {}) => {
  const rawType = String(
    product.productType ||
      product.category ||
      product.listingCategory ||
      product.plantCategory ||
      ""
  )
    .toLowerCase()
    .trim();
  const text = normalizeSearch(
    [
      rawType,
      product.name,
      product.plantName,
      product.description,
      product.hayDetails?.hayType,
      product.barterNotes,
    ]
      .filter(Boolean)
      .join(" ")
  );

  if ((product.marketplaceSection || MARKETPLACE_SECTIONS.GENERAL) === MARKETPLACE_SECTIONS.PLANTS_GARDEN) {
    return "plants";
  }
  if (["produce", "eggs", "meat", "dairy", "handmade", "plants", "hay_straw", "services", "other"].includes(rawType)) {
    return rawType;
  }
  if (text.includes("plant") || text.includes("seed") || text.includes("start")) return "plants";
  if (text.includes("hay") || text.includes("straw") || text.includes("feed")) return "hay_straw";
  if (text.includes("egg")) return "eggs";
  if (text.includes("meat") || text.includes("beef") || text.includes("pork") || text.includes("chicken")) return "meat";
  if (text.includes("milk") || text.includes("cheese") || text.includes("dairy")) return "dairy";
  if (text.includes("handmade") || text.includes("craft") || text.includes("crochet")) return "handmade";
  if (text.includes("tool") || text.includes("equipment") || text.includes("tractor") || text.includes("supply")) return "supplies_services";
  if (text.includes("service") || text.includes("labor") || text.includes("help")) return "services";

  return rawType || "other";
};

const getMarketProductPriceLabel = (product = {}) => {
  const barter = normalizeBarterFields(product);

  if (barter.exchangeType === EXCHANGE_TYPES.TRADE_ONLY) return "Trade Only";
  if (barter.exchangeType === EXCHANGE_TYPES.FREE) return "Free";
  if (isStartsAtPrice(product)) {
    const price = Number(product.price || 0);
    const displayPrice = Number.isInteger(price)
      ? price.toFixed(0)
      : price.toFixed(2);

    return `From $${displayPrice}`;
  }

  return formatProductPrice(product, { includeUnit: false });
};

const getMarketProductLocationLabel = (product, loc, distanceMiles) => {
  if (typeof distanceMiles === "number") {
    return `${distanceMiles.toFixed(distanceMiles < 10 ? 1 : 0)} mi away`;
  }

  return loc.city || loc.state
    ? [loc.city, loc.state].filter(Boolean).join(", ")
    : loc.locationText || product.locationText || "Location not listed";
};

const getMarketProductSortDistance = (viewerLocation, productLocation) => {
  const distance = getSafeDistanceMiles(viewerLocation, productLocation);
  if (distance !== null) return distance;
  return isBroadAreaMatch(viewerLocation, productLocation) ? 0 : Infinity;
};

const getProductLocationSearchText = (product) => {
  const loc = getProductLocation(product);

  return normalizeSearch(
    [
      loc.city,
      loc.state,
      loc.locationText,
      product.county,
      product.locationText,
      product.name,
      product.plantName,
      product.variety,
      product.description,
      product.productType,
      product.plantCategory,
      product.standName,
      product.standCity,
      product.standState,
      product.standLocationText,
    ]
      .filter(Boolean)
      .join(" ")
  );
};

const formatFieldDealLabel = () => "Bid per acre";

const fetchUser = async (userId) => {
  if (!currentUser?.uid) return;

  if (!userId || typeof userId !== "string") {
    console.warn("⚠️ fetchUser skipped invalid userId:", userId);
    return;
  }

  const cleanUserId = userId.trim();

  if (!cleanUserId) {
    console.warn("⚠️ fetchUser skipped empty userId:", userId);
    return;
  }

  if (usersMap[cleanUserId]) return;

  try {
    const userRef = doc(db, "users", cleanUserId);
    const userSnap = await getDoc(userRef);

    setUsersMap((prev) => {
      if (prev[cleanUserId]) return prev;

      return {
        ...prev,
        [cleanUserId]: userSnap.exists()
          ? userSnap.data()
          : {
              name: "Unknown User",
              profilePicture: "",
              rating: 0,
              reviewCount: 0,
            },
      };
    });
  } catch (error) {
    console.error(
      "🔥 JobFeed fetchUser failed for userId:",
      cleanUserId,
      "Code:",
      error.code,
      "Message:",
      error.message
    );

    setUsersMap((prev) => ({
      ...prev,
      [cleanUserId]: {
        name: "Unknown User",
        profilePicture: "",
        rating: 0,
        reviewCount: 0,
      },
    }));
  }
};

  const getCurrentUserRoles = () => {
    const userData = currentUser ? usersMap[currentUser.uid] : null;

    if (Array.isArray(userData?.roles) && userData.roles.length > 0) {
      return userData.roles;
    }

    if (userData?.type) return [userData.type];

    return ["community"];
  };

  const userRoles = getCurrentUserRoles();

  const canViewAsEventPlanner = userRoles.includes("event_planner");
  
    const getJobCollection = (job) => (job?.isDayJob ? "dayJobs" : "fields");

  const getTotalAcres = (job) =>
    Array.isArray(job.fields)
      ? job.fields.reduce((sum, field) => sum + (Number(field.acres) || 0), 0)
      : 0;

  const getJobPrimaryLocation = (job) => {
    if (job.isDayJob) {
      return {
        lat: job?.lat,
        lng: job?.lng,
        city: job?.city,
        state: job?.state,
      };
    }

    const firstField = Array.isArray(job.fields) ? job.fields[0] : null;

    return {
      lat: firstField?.lat,
      lng: firstField?.lng,
      city: firstField?.city,
      state: firstField?.state,
    };
  };

  const FarmTypeBadge = ({ type }) => {
  const meta = getFarmIconMeta(type);

  return (
    <span
      className="farm-type-badge"
      style={{
        background: meta.bg,
        color: meta.color,
        borderColor: `${meta.color}33`,
      }}
    >
      <span>{meta.emoji}</span>
      <strong>{meta.label}</strong>
    </span>
  );
};

  const getProductLocation = (product) =>
    normalizeLocationRecord({
      ...product.location,
      lat: product.lat ?? product.location?.lat ?? product.standLat,
      lng: product.lng ?? product.location?.lng ?? product.standLng,
      city: product.city || product.location?.city || product.standCity || "",
      state: product.state || product.location?.state || product.standState || "",
      county: product.county || product.location?.county || "",
      locationText:
        product.locationText ||
        product.location?.locationText ||
        product.standLocationText ||
        "",
    });

  const applyForDayJob = async (job) => {
  if (!requirePublicJobAction("job_apply_intent", "Create a free account or sign in to apply for jobs.", job?.id)) return;
  if (!currentUser || job.ownerId === currentUser.uid || !job.isDayJob) return;
if (!requireVerifiedEmail(currentUser, userProfile)) return;
if (
  !requirePremiumAction(
    "Premium is required to apply for day jobs. Browsing and buying local products is free."
  )
) {
  return;
}


  const existingApps = applicationsMap[job.id] || [];
  const alreadyApplied = existingApps.find(
    (app) => app.applicantId === currentUser.uid && app.status === "pending"
  );

  if (alreadyApplied) {
    alert("You already applied for this job.");
    return;
  }

  const note = prompt("Optional note to the owner:") || "";

  const userSnap = await getDoc(doc(db, "users", currentUser.uid));
  const userData = userSnap.exists() ? userSnap.data() : {};

  await addDoc(collection(db, "dayJobs", job.id, "applications"), {
    applicantId: currentUser.uid,
    applicantName: userData.name || currentUser.email || "Applicant",
    applicantPhoto: userData.profilePicture || "",
    applicantRating: userData.rating || 0,
    applicantReviewCount: userData.reviewCount || 0,
    note,
    status: "pending",
    createdAt: serverTimestamp(),
  });

  alert("Application sent ✅");
};

const requestToBuyProduct = async (product, requestedQty = 1) => {
  if (!currentUser?.uid) {
    trackUsageEvent("request_to_buy_intent", { feature: "marketplace", action: "intent", relatedId: product?.id });
    trackUsageEvent("auth_gate_open", { feature: "marketplace", action: "request_to_buy_intent", relatedId: product?.id });
  }
  if (!requireSignIn("Create a free account or sign in to request this product.")) return;

  if (!requireVerifiedEmail(currentUser, userProfile)) return;

  const sellerId = product.userId || product.ownerId || product.sellerId;

  if (!sellerId) {
    alert("Seller not found for this product.");
    return;
  }

  if (sellerId === currentUser.uid) {
    alert("You cannot buy your own product.");
    return;
  }

  if (product.status === "sold" || product.status === "out_of_stock") {
    alert("This product is currently out of stock.");
    return;
  }

  const cleanRequestedQty = Math.max(1, Number(requestedQty || 1));
  const availableQty = Number(product.quantityAvailable ?? product.quantity ?? 1);

  if (cleanRequestedQty > availableQty) {
    alert(`Only ${availableQty} available.`);
    return;
  }

  const flowId = `request_to_buy_${product.id}`;
  startTrackedFlow(flowId, {
    feature: "marketplace",
    action: "request_to_buy",
  });
  trackUsageEvent("request_to_buy_started", {
    feature: "marketplace",
    action: "request_to_buy",
    relatedId: product.id,
  });

  try {
    const buyerSnap = await getDoc(doc(db, "users", currentUser.uid));
    const sellerSnap = await getDoc(doc(db, "users", sellerId));

    const buyerData = buyerSnap.exists() ? buyerSnap.data() : {};
    const sellerData = sellerSnap.exists() ? sellerSnap.data() : {};

    const buyerName = buyerData.name || currentUser.email || "Buyer";
    const sellerName = sellerData.name || "Seller";
    const productName = product.name || "Farm Product";

    const productRef = doc(db, "products", product.id);
    let createdOrderId = null;

    await runTransaction(db, async (transaction) => {
      const productSnap = await transaction.get(productRef);

      if (!productSnap.exists()) {
        throw new Error("Product not found.");
      }

      const latestProduct = productSnap.data() || {};

      if (latestProduct.isArchived) {
        throw new Error("This product is no longer available.");
      }

      if (
        latestProduct.status === "sold" ||
        latestProduct.status === "out_of_stock"
      ) {
        throw new Error("This product is currently out of stock.");
      }

      const currentQty =
        typeof latestProduct.quantityAvailable === "number"
          ? latestProduct.quantityAvailable
          : typeof latestProduct.quantity === "number"
          ? latestProduct.quantity
          : 1;

      if (currentQty <= 0) {
        throw new Error("This product is currently out of stock.");
      }

      if (cleanRequestedQty > currentQty) {
        throw new Error(`Only ${currentQty} available.`);
      }

      const orderRef = doc(collection(db, "productOrders"));
      createdOrderId = orderRef.id;

      transaction.set(orderRef, {
        productId: product.id,
        productName,
        productImage: latestProduct.image || product.image || "",
        productPrice: Number(latestProduct.price || product.price || 0),
        productUnit: latestProduct.unit || product.unit || "item",

        sellerId,
        sellerName,

        buyerId: currentUser.uid,
        buyerName,
        buyerEmail: currentUser.email || "",
        buyerPhoto: buyerData.profilePicture || "",

        status: "pending",
        quantity: cleanRequestedQty,
        message: `I would like to buy ${cleanRequestedQty} ${
          latestProduct.unit || product.unit || "item"
        }${cleanRequestedQty === 1 ? "" : "s"}.`,
        createdAt: serverTimestamp(),
      });

      transaction.update(productRef, {
        lastRequestedBy: currentUser.uid,
        lastRequestedByName: buyerName,
        lastRequestedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    });

    const requestMessage = `🛒 ${buyerName} requested ${cleanRequestedQty} ${
      product.unit || "item"
    }${cleanRequestedQty === 1 ? "" : "s"} of "${productName}".`;

    const tradeDraft = getTradeMessageDraft(product);
    const conversationId = await findOrCreateConversation({
      ownerId: sellerId,
      isDayJob: false,
      id: product.id,
      grassType: productName,
      fields: [],
    });

    if (!conversationId) {
      throw new Error("Could not create conversation with seller.");
    }

    await addDoc(collection(db, "conversations", conversationId, "messages"), {
      text: `${requestMessage}\n\nPlease confirm if this quantity is available.${
        tradeDraft ? `\n\n${tradeDraft}` : ""
      }`,
      senderId: currentUser.uid,
      seenBy: [currentUser.uid],
      createdAt: serverTimestamp(),
      productId: product.id,
      productName,
      productOrderId: createdOrderId,
      quantity: cleanRequestedQty,
      messageType: "product_request",
    });

    await updateDoc(doc(db, "conversations", conversationId), {
      lastMessage: requestMessage,
      lastMessageAt: serverTimestamp(),
      lastSenderId: currentUser.uid,
      [`unreadCount.${sellerId}`]: increment(1),

      productRequestSnapshot: {
        orderId: createdOrderId,
        productId: product.id,
        productName,
        productImage: product.image || "",
        productPrice: Number(product.price || 0),
        productUnit: product.unit || "item",
        quantity: cleanRequestedQty,
        buyerId: currentUser.uid,
        buyerName,
        sellerId,
        sellerName,
        status: "pending",
      },
    });

    await addDoc(collection(db, "notifications"), {
      toUserId: sellerId,
      userId: sellerId,
      recipientId: sellerId,
      type: "product_request",
      title: "New product request",
      message: `${buyerName} wants to buy ${cleanRequestedQty} ${
        product.unit || "item"
      }${cleanRequestedQty === 1 ? "" : "s"} of "${productName}".`,
      productId: product.id,
      productName,
      productOrderId: createdOrderId,
      quantity: cleanRequestedQty,
      buyerId: currentUser.uid,
      buyerName,
      read: false,
      createdAt: serverTimestamp(),
    });

    setSelectedJob({
      ownerId: sellerId,
      isDayJob: false,
      fields: [],
      grassType: productName,
    });

    setActiveConversationId(conversationId);
    setChatInitialMessage("");
    setSelectedProduct(null);
    setRequestQuantity(1);
    completeTrackedFlow(flowId);
    trackUsageEvent("request_to_buy_completed", {
      feature: "marketplace",
      action: "request_to_buy",
      relatedId: product.id,
      status: "completed",
    });

    alert(
      `Buy request sent ✅ Requested ${cleanRequestedQty} ${
        product.unit || "item"
      }${cleanRequestedQty === 1 ? "" : "s"}. Stock will update after the seller confirms the sale.`
    );
  } catch (error) {
    console.error("Buy request failed:", error);
    trackUsageEvent("form_error_shown", {
      feature: "marketplace",
      action: "request_to_buy",
      relatedId: product.id,
      status: "error",
      errorCode: error.code || "request_failed",
      errorSummary: error.message || "Could not send buy request",
    });
    alert(error.message || "Could not send buy request.");
  }
};

const confirmProductSale = async (order) => {
  if (!currentUser?.uid) return;

  if (order.sellerId !== currentUser.uid) {
    alert("Only the seller can confirm this sale.");
    return;
  }

  const confirmed = window.confirm(
    `Confirm sale of ${order.quantity} ${order.productUnit || "item"}${
      Number(order.quantity || 1) === 1 ? "" : "s"
    }? This will update the product stock.`
  );

  if (!confirmed) return;

  try {
    let completedOrderData = null;
    let completedProductData = null;
    let soldQtyForFollowUp = Number(order.quantity || 1);

    await runTransaction(db, async (transaction) => {
      const orderRef = doc(db, "productOrders", order.id);
      const productRef = doc(db, "products", order.productId);

      const orderSnap = await transaction.get(orderRef);
      const productSnap = await transaction.get(productRef);

      if (!orderSnap.exists()) {
        throw new Error("Order not found.");
      }

      if (!productSnap.exists()) {
        throw new Error("Product not found.");
      }

      const latestOrder = orderSnap.data();
      const latestProduct = productSnap.data();
      completedOrderData = latestOrder;
      completedProductData = latestProduct;

      if (latestOrder.status === "completed") {
        throw new Error("This sale is already completed.");
      }

      if (latestOrder.status === "declined") {
        throw new Error("This sale was declined.");
      }

      const currentQty =
        typeof latestProduct.quantityAvailable === "number"
          ? latestProduct.quantityAvailable
          : typeof latestProduct.quantity === "number"
          ? latestProduct.quantity
          : 0;

      const soldQty = Number(latestOrder.quantity || order.quantity || 1);
      soldQtyForFollowUp = soldQty;

      if (soldQty > currentQty) {
        throw new Error(`Only ${currentQty} available.`);
      }

      const newQty = Math.max(currentQty - soldQty, 0);

      transaction.update(productRef, {
  quantityAvailable: newQty,
  status: newQty <= 0 ? "out_of_stock" : "active",
  availabilityStatus:
    newQty <= 0
      ? PRODUCT_AVAILABILITY.OUT_OF_STOCK
      : PRODUCT_AVAILABILITY.AVAILABLE_NOW,
  updatedAt: serverTimestamp(),
});

      transaction.update(orderRef, {
        status: "completed",
        completedAt: serverTimestamp(),
        confirmedBy: currentUser.uid,
      });
    });

    alert("Sale confirmed and stock updated ✅");

    if (completedProductData?.linkedInventoryId) {
      const reduceInventory = window.confirm(
        `Reduce inventory by ${soldQtyForFollowUp} ${completedProductData.unit || order.productUnit || "units"}?`
      );

      if (reduceInventory) {
        const inventoryRef = doc(db, "farmInventory", completedProductData.linkedInventoryId);
        const inventorySnap = await getDoc(inventoryRef);

        if (inventorySnap.exists() && inventorySnap.data().ownerId === currentUser.uid) {
          const inventory = inventorySnap.data();
          await updateDoc(inventoryRef, {
            quantity: Math.max(Number(inventory.quantity || 0) - soldQtyForFollowUp, 0),
            updatedAt: serverTimestamp(),
          });
        }
      }
    }

    if (window.confirm("Add this completed product sale to Money Log?")) {
      await addDoc(collection(db, "farmMoneyLogs"), {
        ownerId: currentUser.uid,
        type: "income",
        title: `Sale: ${completedOrderData?.productName || order.productName || "Farm product"}`,
        category: "product_sales",
        amount:
          Number(completedOrderData?.totalPrice || order.totalPrice || 0) ||
          Number(completedOrderData?.price || order.price || completedProductData?.price || 0) * soldQtyForFollowUp,
        date: new Date().toISOString().slice(0, 10),
        linkedRecordType: "product_order",
        linkedRecordId: order.id,
        notes: `Product ID: ${order.productId || ""}`,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    }
  } catch (error) {
    console.error("Confirm sale failed:", error);
    alert(error.message || "Could not confirm sale.");
  }
};

const getReviewPeopleForJob = (job) => {
  const ownerId = job.ownerId;
  const workerId =
    job.acceptedBy ||
    job.acceptedOperator ||
    job.completedBy ||
    job.acceptedApplicantId;

  return { ownerId, workerId };
};

const currentUserCanReviewJob = (job) => {
  if (!currentUser?.uid || (job.status || "open") !== "completed") return false;

  const { ownerId, workerId } = getReviewPeopleForJob(job);

  return currentUser.uid === ownerId || currentUser.uid === workerId;
};

const getRevieweeForJob = (job) => {
  const { ownerId, workerId } = getReviewPeopleForJob(job);

  if (currentUser?.uid === ownerId) {
    return {
      revieweeId: workerId,
      revieweeName: job.acceptedByName || job.completedByName || "Worker",
      reviewerRole: "owner",
      revieweeRole: "worker",
    };
  }

  if (currentUser?.uid === workerId) {
    return {
      revieweeId: ownerId,
      revieweeName: usersMap[ownerId]?.name || "Job Owner",
      reviewerRole: "worker",
      revieweeRole: "owner",
    };
  }

  return null;
};

const hasReviewedJob = (job) => {
  if (!currentUser?.uid || !job?.id) return false;
  return Boolean(reviewsMap[`${job.id}_${currentUser.uid}`]);
};

const getJobReviewTitle = (job) => {
  if (job.isDayJob) return job.title || "Day Job";

  const acres = getTotalAcres(job).toFixed(2);
  const crop = job.grassType || job.fields?.[0]?.category || "Field Job";

  return `${acres} acre ${crop}`;
};

const openReviewModal = (job) => {
  const reviewee = getRevieweeForJob(job);

  if (!reviewee?.revieweeId) {
    alert("Could not find the other person for this job.");
    return;
  }

  setReviewTarget({
    job,
    ...reviewee,
  });
  setReviewRating(5);
  setReviewComment("");
};



const getBoothAvailability = (event) => {
  const categories = event?.boothCategories || [];

  const totalSpots = categories.reduce(
    (sum, cat) => sum + Number(cat.spotsAvailable || 0),
    0
  );

  const reservedSpots = categories.reduce(
    (sum, cat) => sum + Number(cat.spotsReserved || 0),
    0
  );

  return {
    totalSpots,
    reservedSpots,
    remainingSpots: Math.max(totalSpots - reservedSpots, 0),
  };
};

const isEventRegistrationOpen = (event) =>
  (event?.registrationStatus || "open") === "open" &&
  event?.detailsComingSoon !== true &&
  event?.reservationEnabled === true;

const getEventReservationButtonLabel = (event) => {
  if (event?.detailsComingSoon === true) return "Details Coming Soon";
  if ((event?.registrationStatus || "open") === "opens_later") {
    return event?.registrationOpensAt
      ? `Opens ${new Date(`${event.registrationOpensAt}T00:00:00`).toLocaleDateString()}`
      : "Registration Opens Later";
  }
  if ((event?.registrationStatus || "open") === "closed") {
    return "Registration Closed";
  }
  return "Reserve Booth";
};



const openReservationModal = (event) => {
  if (!requirePublicJobAction("event_participation_intent", "Create a free account or sign in to reserve a booth.", event?.id)) {
    return;
  }

  if (!isEventRegistrationOpen(event)) {
    alert("Registration is not open for this event yet.");
    return;
  }

  const categories = event.boothCategories || [];
  const firstOpenCategory = categories.find((cat) => getCategoryRemaining(cat) > 0);

  setSelectedEvent(null); // ✅ closes event card modal first
  setReservationEvent(event);
  setReservationCategoryId(firstOpenCategory?.id || "");
  setReservationNote("");

};

const getCategoryRemaining = (cat) => {
  return Math.max(
    Number(cat.spotsAvailable || 0) - Number(cat.spotsReserved || 0),
    0
  );
};

const submitJobReview = async () => {
  if (!currentUser?.uid || !reviewTarget?.job) return;
  if (!requireVerifiedEmail(currentUser, userProfile)) return;

  const job = reviewTarget.job;

  if (hasReviewedJob(job)) {
    alert("You already reviewed this job.");
    setReviewTarget(null);
    return;
  }

  if (!reviewComment.trim()) {
    alert("Please add a short comment.");
    return;
  }

  try {
    setSubmittingReview(true);

    const reviewerSnap = await getDoc(doc(db, "users", currentUser.uid));
    const reviewerData = reviewerSnap.exists() ? reviewerSnap.data() : {};

    const revieweeSnap = await getDoc(doc(db, "users", reviewTarget.revieweeId));
    const revieweeData = revieweeSnap.exists() ? revieweeSnap.data() : {};

    const ratingNumber = Number(reviewRating || 5);

    await addDoc(collection(db, "reviews"), {
      jobId: job.id,
      jobType: job.isDayJob ? "dayJob" : "field",

      reviewerId: currentUser.uid,
      reviewerName: reviewerData.name || currentUser.email || "Reviewer",
      reviewerPhoto: reviewerData.profilePicture || "",

      revieweeId: reviewTarget.revieweeId,
      revieweeName:
        revieweeData.name || reviewTarget.revieweeName || "User",
      revieweePhoto: revieweeData.profilePicture || "",

      reviewerRole: reviewTarget.reviewerRole,
      revieweeRole: reviewTarget.revieweeRole,

      rating: ratingNumber,
      comment: reviewComment.trim(),

      jobTitle: getJobReviewTitle(job),
      jobLocation: job.isDayJob
        ? formatLocation(job.city, job.state)
        : getFieldJobDisplayLocation(job),
      jobCompletedAt: job.completedAt || null,

      jobImage: job.jobImage || job.image || "",
      completionImage: job.completionImage || "",

      createdAt: serverTimestamp(),
    });

    // Recalculate reviewee rating from existing + new review
    const existingReviewsSnap = await getDocs(
      query(collection(db, "reviews"), where("revieweeId", "==", reviewTarget.revieweeId))
    );

    const existingRatings = existingReviewsSnap.docs
      .map((docSnap) => Number(docSnap.data().rating || 0))
      .filter((rating) => rating > 0);

    const allRatings = [...existingRatings, ratingNumber];
    const average =
      allRatings.reduce((sum, rating) => sum + rating, 0) / allRatings.length;

    await updateDoc(doc(db, "users", reviewTarget.revieweeId), {
      rating: Number(average.toFixed(2)),
      reviewCount: allRatings.length,
    });

    alert("Review submitted ✅");

    setReviewTarget(null);
    setReviewComment("");
    setReviewRating(5);
  } catch (error) {
    console.error("Review submit failed:", error);
    alert(error.message || "Could not submit review.");
  } finally {
    setSubmittingReview(false);
  }
};

const submitEventReservation = async () => {
  const event = reservationEvent;

  if (!event?.id) {
    alert("Event missing. Please close and reopen the reservation window.");
    return;
  }

  if (!requirePublicJobAction("event_participation_intent", "Create a free account or sign in to reserve a spot.", event.id)) return;

  if (!requireVerifiedEmail(currentUser, userProfile)) return;

  if (!isEventRegistrationOpen(event)) {
    alert("Registration is not open for this event yet.");
    return;
  }

  if (!reservationCategoryId) {
    alert("Please choose a booth type.");
    return;
  }

  const selectedCategory = (event.boothCategories || []).find(
    (cat) => cat.id === reservationCategoryId
  );

  if (!selectedCategory) {
    alert("Booth category not found.");
    return;
  }

  const hostId = event.ownerId || event.userId || event.hostId;

  if (!hostId) {
    alert("Event host not found.");
    return;
  }

  try {
    setSubmittingReservation(true);

    const userSnap = await getDoc(doc(db, "users", currentUser.uid));
    const userData = userSnap.exists() ? userSnap.data() : {};

    await addDoc(collection(db, "eventReservations"), {
      eventId: event.id,
      eventTitle: event.title || "Event",
       userId,
       userName,
      eventOwnerId: hostId,
      hostId,

      requesterId: currentUser.uid,
      requesterName: userData.name || currentUser.email || "Vendor",
      requesterEmail: currentUser.email || "",
      requesterPhoto: userData.profilePicture || "",

      boothCategory: selectedCategory.id,
      boothCategoryLabel: selectedCategory.label,
      quantity: 1,
      note: reservationNote || "I would like to reserve a vendor spot.",

      status: "pending",
      paymentStatus: "pay_in_person",
      createdAt: serverTimestamp(),
    });

    alert("Reservation request sent ✅");

    setReservationEvent(null);
    setReservationCategoryId("");
    setReservationNote("");
  } catch (err) {
    console.error("Reservation request error:", err);
    alert(err.message || "Could not send reservation request.");
  } finally {
    setSubmittingReservation(false);
  }
};


const approveEventReservation = async (reservation) => {
  try {
    await updateDoc(doc(db, "eventReservations", reservation.id), {
      status: "approved",
      approvedAt: serverTimestamp(),
    });

    alert("Vendor approved. Waiting for payment.");
  } catch (err) {
    console.error(err);
    alert("Error approving reservation");
  }
};

const acceptEventReservationRequest = async (reservation) => {
  try {
    // update status
    await updateDoc(doc(db, "eventReservations", reservation.id), {
      status: "accepted",
      acceptedAt: serverTimestamp(),
    });

    // 🔥 send message to vendor
    const conversationId = await findOrCreateConversation({
      ownerId: reservation.requesterId, // vendor
      isDayJob: false,
      id: reservation.eventId,
      grassType: reservation.eventTitle || "Event Reservation",
      fields: [],
    });

    await addDoc(collection(db, "conversations", conversationId, "messages"), {
      text: `🎉 Your booth reservation for "${reservation.eventTitle}" has been ACCEPTED!

You now have a spot reserved.

👉 Please pay in person at the event to finalize your reservation.`,
      senderId: currentUser.uid,
      seenBy: [currentUser.uid],
      createdAt: serverTimestamp(),
    });

    alert("Reservation accepted. Vendor has been notified.");

  } catch (err) {
    console.error(err);
    alert("Error accepting reservation");
  }
};

const archiveEvent = async (event) => {
  if (!currentUser?.uid) return;

  const eventOwnerId = event.ownerId || event.userId || event.hostId;

  if (eventOwnerId !== currentUser.uid) {
    alert("Only the event owner can remove this event.");
    return;
  }

  const confirmed = window.confirm(
    "Remove this event from the Events page? This will hide it, but not permanently delete it."
  );

  if (!confirmed) return;

  try {
    await updateDoc(doc(db, "communityEvents", event.id), {
      isArchived: true,
      status: "archived",
      archivedAt: serverTimestamp(),
      archivedBy: currentUser.uid,
    });

    setSelectedEvent(null);
    alert("Event removed ✅");
  } catch (error) {
    console.error("Error removing event:", error);
    alert("Could not remove event. Please try again.");
  }
};

const quickUpdateEvent = async (event) => {
  if (!currentUser?.uid) return;

  const eventOwnerId = event.ownerId || event.userId || event.hostId;

  if (eventOwnerId !== currentUser.uid) {
    alert("Only the event owner can update this event.");
    return;
  }

  const newTitle = prompt("Update event title:", event.title || "");
  if (newTitle === null) return;

  const newDate = prompt("Update event date YYYY-MM-DD:", event.eventDate || "");
  if (newDate === null) return;

  const newLocation = prompt(
    "Update event location:",
    event.locationText || ""
  );
  if (newLocation === null) return;

  try {
    await updateDoc(doc(db, "communityEvents", event.id), {
      title: newTitle.trim() || event.title || "Community Event",
      eventDate: newDate.trim() || event.eventDate || "",
      locationText: newLocation.trim() || event.locationText || "",
      updatedAt: serverTimestamp(),
    });

    alert("Event updated ✅");
  } catch (error) {
    console.error("Error updating event:", error);
    alert("Could not update event.");
  }
};

const confirmPaidEventReservation = async (reservation) => {
  try {
    await runTransaction(db, async (transaction) => {
      const reservationRef = doc(db, "eventReservations", reservation.id);
      const reservationSnap = await transaction.get(reservationRef);

      if (!reservationSnap.exists()) {
        throw new Error("Reservation not found.");
      }

      const latestReservation = reservationSnap.data();

      if (latestReservation.status === "completed") {
        throw new Error("This reservation is already paid and confirmed.");
      }

      if (latestReservation.status === "declined") {
        throw new Error("This reservation was already declined.");
      }

      if (latestReservation.status !== "accepted") {
        throw new Error("Reservation must be accepted before confirming payment.");
      }

      transaction.update(reservationRef, {
  status: "completed",
  paymentStatus: "paid_in_person",
  paidAt: serverTimestamp(),
  completedAt: serverTimestamp(),
  isArchivedFromHostDashboard: true,
});
    });

    alert("Payment confirmed ✅");
  } catch (err) {
    console.error(err);
    alert(err.message || "Error confirming payment");
  }
};
  
  const submitBid = async (job) => {
    if (!requirePublicJobAction("job_bid_intent", "Create a free account or sign in to bid on field jobs.", job?.id)) return;
    if (!currentUser || job.ownerId === currentUser.uid || job.isDayJob) return;
if (!requireVerifiedEmail(currentUser, userProfile)) return;
if (
  !requirePremiumAction(
    "Premium is required to bid on field jobs. Browsing and buying local products is free."
  )
) {
  return;
}


    const existingBids = bidsMap[job.id] || [];
    const alreadyBid = existingBids.find(
      (bid) => bid.bidderId === currentUser.uid && bid.status === "pending"
    );

    if (alreadyBid) {
      alert("You already placed a bid on this job.");
      return;
    }

  

    const priceInput = prompt("Enter your bid per acre:");
    if (!priceInput) return;

    const pricePerAcre = Number(priceInput);

    if (Number.isNaN(pricePerAcre) || pricePerAcre <= 0) {
      alert("Please enter a valid price per acre.");
      return;
    }

    const note = prompt("Optional note to the farmer:") || "";

    const userSnap = await getDoc(doc(db, "users", currentUser.uid));
    const userData = userSnap.exists() ? userSnap.data() : {};

    const totalAcres = getTotalAcres(job);
    const totalBid = Number((pricePerAcre * totalAcres).toFixed(2));

    const bidderName = userData.name || currentUser.email || "Operator";

const bidRef = await addDoc(collection(db, "fields", job.id, "bids"), {
  bidderId: currentUser.uid,
  bidderName,
  bidderPhoto: userData.profilePicture || "",
  bidderRating: userData.rating || 0,
  bidderReviewCount: userData.reviewCount || 0,
  pricePerAcre,
  totalAcres,
  totalBid,
  note,
  status: "pending",
  createdAt: serverTimestamp(),
});

const conversationId = await findOrCreateConversation(job);

if (conversationId) {
  const bidMessage = `💰 ${bidderName} placed a bid on this field job.

Bid: $${pricePerAcre}/acre
Total: $${totalBid}${note ? `\nNote: ${note}` : ""}`;

  await addDoc(collection(db, "conversations", conversationId, "messages"), {
    text: bidMessage,
    senderId: currentUser.uid,
    seenBy: [currentUser.uid],
    createdAt: serverTimestamp(),
    jobId: job.id,
    bidId: bidRef.id,
    messageType: "field_bid",
  });

  await updateDoc(doc(db, "conversations", conversationId), {
    lastMessage: `${bidderName} placed a bid: $${totalBid}`,
    lastMessageAt: serverTimestamp(),
    lastSenderId: currentUser.uid,
    [`unreadCount.${job.ownerId}`]: increment(1),
  });

  setSelectedJob(job);
  setActiveConversationId(conversationId);
}

await addDoc(collection(db, "notifications"), {
  toUserId: job.ownerId,
  userId: job.ownerId,
  recipientId: job.ownerId,
  type: "field_bid",
  title: "New field job bid",
  message: `${bidderName} bid $${pricePerAcre}/acre, total $${totalBid}.`,
  fieldId: job.id,
  bidId: bidRef.id,
  bidderId: currentUser.uid,
  bidderName,
  route: "/jobs",
  read: false,
  createdAt: serverTimestamp(),
});

alert("Bid submitted ✅ Chat started with the job owner.");
  };

  const acceptDayJobApplication = async (job, application) => {
  if (!currentUser || job.ownerId !== currentUser.uid || !job.isDayJob) return;

  const jobRef = doc(db, "dayJobs", job.id);
  const appRef = doc(db, "dayJobs", job.id, "applications", application.id);

  await updateDoc(jobRef, {
    status: "in_progress",
    acceptedBy: application.applicantId,
    acceptedByName: application.applicantName || "",
    acceptedByPhoto: application.applicantPhoto || "",
    acceptedApplicationId: application.id,
    acceptedAt: serverTimestamp(),
  });

  await updateDoc(appRef, {
    status: "accepted",
    acceptedAt: serverTimestamp(),
  });

  const otherApps = applicationsMap[job.id] || [];
  const batch = writeBatch(db);

  otherApps.forEach((otherApp) => {
    if (otherApp.id !== application.id && otherApp.status !== "rejected") {
      const otherAppRef = doc(db, "dayJobs", job.id, "applications", otherApp.id);

      batch.update(otherAppRef, {
        status: "rejected",
        rejectedAt: serverTimestamp(),
        rejectedReason: "Another applicant was accepted",
      });
    }
  });

  await batch.commit();

  alert("Applicant accepted ✅");
};

const rejectDayJobApplication = async (job, application) => {
  if (!currentUser || job.ownerId !== currentUser.uid || !job.isDayJob) return;

  await updateDoc(doc(db, "dayJobs", job.id, "applications", application.id), {
    status: "rejected",
    rejectedAt: serverTimestamp(),
  });
};

  const acceptBid = async (job, bid) => {
    if (!currentUser || job.ownerId !== currentUser.uid || job.isDayJob) return;

    try {
      const jobRef = doc(db, "fields", job.id);
      const bidRef = doc(db, "fields", job.id, "bids", bid.id);

      await updateDoc(jobRef, {
        status: "in_progress",
        acceptedOperator: bid.bidderId,
        acceptedBy: bid.bidderId,
        acceptedByName: bid.bidderName || "",
        acceptedByPhoto: bid.bidderPhoto || "",
        acceptedBidId: bid.id,
        acceptedBidAmount: bid.totalBid,
        acceptedPricePerAcre: bid.pricePerAcre,
        acceptedAt: serverTimestamp(),
      });

      await updateDoc(bidRef, {
        status: "accepted",
        acceptedAt: serverTimestamp(),
      });

      const otherBids = bidsMap[job.id] || [];
      const batch = writeBatch(db);

      otherBids.forEach((otherBid) => {
        if (otherBid.id !== bid.id && otherBid.status !== "rejected") {
          const otherBidRef = doc(db, "fields", job.id, "bids", otherBid.id);

          batch.update(otherBidRef, {
            status: "rejected",
            rejectedAt: serverTimestamp(),
            rejectedReason: "Another bid was accepted",
          });
        }
      });

      await batch.commit();

      const ownerSnap = await getDoc(doc(db, "users", currentUser.uid));
      const bidderSnap = await getDoc(doc(db, "users", bid.bidderId));

      const ownerData = ownerSnap.exists() ? ownerSnap.data() : {};
      const bidderData = bidderSnap.exists() ? bidderSnap.data() : {};

      const conversationRef = await addDoc(collection(db, "conversations"), {
        users: [currentUser.uid, bid.bidderId],
        participantsInfo: {
          [currentUser.uid]: {
            name: ownerData.name || currentUser.email || "Job Owner",
            email: currentUser.email || "",
            photo: ownerData.profilePicture || "",
            type: ownerData.type || "landowner",
          },
          [bid.bidderId]: {
            name: bidderData.name || bid.bidderName || "Operator",
            email: bidderData.email || "",
            photo: bidderData.profilePicture || bid.bidderPhoto || "",
            type: bidderData.type || "farmer",
          },
        },
        jobSnapshot: {
          jobId: job.id,
          jobType: "field",
          title: job.grassType || "Field job",
          acres: getTotalAcres(job),
          city: job.city || "",
          state: job.state || "",
          acceptedBidAmount: bid.totalBid,
          acceptedPricePerAcre: bid.pricePerAcre,
        },
        lastMessage: "Bid accepted. You can now chat about the job.",
        lastSenderId: currentUser.uid,
        lastMessageAt: serverTimestamp(),
        unreadCount: {
          [currentUser.uid]: 0,
          [bid.bidderId]: 1,
        },
        createdAt: serverTimestamp(),
      });

      await addDoc(collection(db, "conversations", conversationRef.id, "messages"), {
        text: "Your bid was accepted. You can now chat here about the job details.",
        senderId: currentUser.uid,
        seenBy: [currentUser.uid],
        createdAt: serverTimestamp(),
      });

      setSelectedJob(job);
      setActiveConversationId(conversationRef.id);
    } catch (error) {
      console.error("Failed to accept bid:", error);
      alert("Something went wrong accepting this bid.");
    }
  };

  const rejectBid = async (job, bid) => {
    if (!currentUser || job.ownerId !== currentUser.uid || job.isDayJob) return;

    await updateDoc(doc(db, "fields", job.id, "bids", bid.id), {
      status: "rejected",
      rejectedAt: serverTimestamp(),
    });
  };

  const completeJob = async (job) => {
    if (!job.acceptedBy) return;

    await updateDoc(doc(db, getJobCollection(job), job.id), {
      status: "completed",
      completedBy: job.acceptedBy,
      completedByName: job.acceptedByName || "",
      completedByPhoto: job.acceptedByPhoto || "",
      completedAt: serverTimestamp(),
    });

    alert("Job marked completed ✅");

    if (currentUser?.uid === job.ownerId && window.confirm("Add this completed job to Money Log?")) {
      await addDoc(collection(db, "farmMoneyLogs"), {
        ownerId: currentUser.uid,
        type: "income",
        title: job.title || job.jobTypeLabel || job.grassType || "Completed farm job",
        category: "job_income",
        amount: Number(job.acceptedBidAmount || job.acceptedAmount || job.payAmount || 0),
        date: new Date().toISOString().slice(0, 10),
        linkedRecordType: job.isDayJob ? "day_job" : "field_job",
        linkedRecordId: job.id,
        notes: `Completed by ${job.acceptedByName || job.completedByName || "worker"}`,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    }
  };

  const findOrCreateConversation = async (job, { skipAuthGate = false } = {}) => {
    if (!currentUser?.uid) {
      if (!skipAuthGate) {
        requireSignIn("Create a free account or sign in to message this member.");
      }
      return null;
    }
    if (!job?.ownerId) return null;
    if (job.ownerId === currentUser.uid) return null;

    const q = query(
      collection(db, "conversations"),
      where("users", "array-contains", currentUser.uid)
    );

    const snapshot = await getDocs(q);

    const existingConversation = snapshot.docs.find((docSnap) => {
      const data = docSnap.data();
      
      
      
      return (
        Array.isArray(data.users) &&
        data.users.includes(currentUser.uid) &&
        data.users.includes(job.ownerId)
      );
    });

    if (existingConversation) return existingConversation.id;

    const newConversationRef = await addDoc(collection(db, "conversations"), {
      users: [currentUser.uid, job.ownerId],
      participantsInfo: {
        [currentUser.uid]: {
          name: currentUser.displayName || currentUser.email || "You",
          email: currentUser.email || "",
          photo: currentUser.photoURL || "",
        },
        [job.ownerId]: {
          name: usersMap[job.ownerId]?.name || "User",
          email: usersMap[job.ownerId]?.email || "",
          photo: usersMap[job.ownerId]?.profilePicture || "",
          type: usersMap[job.ownerId]?.type || "",
        },
      },
      lastMessage: "",
      unreadCount: {
        [currentUser.uid]: 0,
        [job.ownerId]: 0,
      },
      createdAt: serverTimestamp(),
      jobSnapshot: job.isDayJob
        ? {
            jobId: job.id,
            type: "dayJob",
            title: job.title || "",
            category: job.category || "",
          }
        : {
            jobId: job.id,
            type: "field",
            grassType: job.grassType || "",
            totalAcres: getTotalAcres(job),
          },
    });

    return newConversationRef.id;
  };

  const openChatForJob = async (job) => {
    setSelectedJob(job);
    const conversationId = await findOrCreateConversation(job);
    if (!conversationId) return;
    setActiveConversationId(conversationId);
  };

  useEffect(() => {
    if (currentUser?.uid) fetchUser(currentUser.uid);
  }, [currentUser?.uid]);

  useEffect(() => {
    const fieldsQuery = currentUser?.uid
      ? collection(db, "fields")
      : query(collection(db, "fields"), where("status", "==", "open"));
    const unsubscribe = onSnapshot(fieldsQuery, (snapshot) => {
      const jobs = snapshot.docs
        .map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }))
        .filter((job) => !job.isArchived);

      setFields(jobs);
      if (currentUser?.uid) {
        jobs.forEach((job) => fetchUser(job.ownerId));
      }
    });

    return () => unsubscribe();
  }, [currentUser?.uid]);

  const isUpcomingVisibleEvent = (event) => {
  if (!event) return false;

  if (event.isArchived === true) return false;
  if (event.status === "expired") return false;

  // If an event has no date, keep it visible so new/unfinished posts don't vanish by accident.
  if (!event.eventDate) return true;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  let eventDate;

  if (event.eventDate?.toDate) {
    eventDate = event.eventDate.toDate();
  } else {
    eventDate = new Date(event.eventDate);
  }

  if (Number.isNaN(eventDate.getTime())) return true;

  eventDate.setHours(0, 0, 0, 0);

  return eventDate >= today;
};

useEffect(() => {
  const eventsQuery = currentUser?.uid
    ? collection(db, "communityEvents")
    : query(collection(db, "communityEvents"), where("status", "in", ["active", "published"]));
  const unsub = onSnapshot(eventsQuery, (snapshot) => {
    const list = snapshot.docs
      .map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }))
      .filter(isUpcomingVisibleEvent);

    setEvents(list);
  });

  return () => unsub();
}, []);

useEffect(() => {
  if (!currentUser?.uid) {
    setProductReviews([]);
    return;
  }

  const unsub = onSnapshot(collection(db, "productReviews"), (snapshot) => {
    const list = snapshot.docs.map((docSnap) => ({
      id: docSnap.id,
      ...docSnap.data(),
    }));

    setProductReviews(list);
  });

  return () => unsub();
}, [currentUser?.uid]);

useEffect(() => {
  if (!currentUser?.uid) return;

  const q = query(
    collection(db, "eventReservations"),
    where("eventOwnerId", "==", currentUser.uid)
  );

  const unsub = onSnapshot(q, (snap) => {
    const list = snap.docs
      .map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }))
      .filter((reservation) => reservation.isArchivedFromHostDashboard !== true);

    setEventReservations(list);
  });

  return () => unsub();
}, [currentUser?.uid]);

  useEffect(() => {
    const dayJobsQuery = currentUser?.uid
      ? collection(db, "dayJobs")
      : query(collection(db, "dayJobs"), where("status", "==", "open"));
    const unsubscribe = onSnapshot(dayJobsQuery, (snapshot) => {
      const jobs = snapshot.docs
        .map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
          isDayJob: true,
        }))
        .filter((job) => !job.isArchived);

      setDayJobs(jobs);
      if (currentUser?.uid) {
        jobs.forEach((job) => fetchUser(job.ownerId));
      }
    });

    return () => unsubscribe();
  }, [currentUser?.uid]);

  useEffect(() => {
    const productsQuery = currentUser?.uid
      ? collection(db, "products")
      : query(collection(db, "products"), where("status", "==", "active"));
    const unsubscribe = onSnapshot(productsQuery, (snapshot) => {
      const productList = snapshot.docs
        .map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }))
        .filter((product) => !product.isArchived);

      setProducts(productList);
      if (currentUser?.uid) {
        productList.forEach((product) => {
          const sellerId = product.userId || product.ownerId || product.sellerId;
          fetchUser(sellerId);
        });
      }
    });

    return () => unsubscribe();
  }, [currentUser?.uid]);

  useEffect(() => {
    const standsQuery = query(
      collection(db, "farmStands"),
      where("status", "in", ["active", "open"])
    );
    const unsubscribe = onSnapshot(standsQuery, (snapshot) => {
      setMarketplaceFarmStands(
        snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }))
      );
    });

    return () => unsubscribe();
  }, []);

   useEffect(() => {
  if (!currentUser?.uid || !dayJobs.length) {
    setApplicationsMap({});
    return;
  }

  const unsubscribers = dayJobs.map((job) =>
    onSnapshot(collection(db, "dayJobs", job.id, "applications"), (snapshot) => {
      const apps = snapshot.docs.map((appDoc) => ({
        id: appDoc.id,
        ...appDoc.data(),
      }));

      setApplicationsMap((prev) => ({
        ...prev,
        [job.id]: apps,
      }));
    })
  );

  return () => {
    unsubscribers.forEach((unsubscribe) => unsubscribe());
  };
}, [currentUser?.uid, dayJobs]);

  useEffect(() => {
    if (!currentUser?.uid || !fields.length) {
      setBidsMap({});
      return;
    }

    const unsubscribers = fields.map((job) =>
      onSnapshot(collection(db, "fields", job.id, "bids"), (snapshot) => {
        const bids = snapshot.docs.map((bidDoc) => ({
          id: bidDoc.id,
          ...bidDoc.data(),
        }));

        setBidsMap((prev) => ({
          ...prev,
          [job.id]: bids,
        }));
      })
    );

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [currentUser?.uid, fields]);

  useEffect(() => {
  if (!currentUser?.uid) {
    setReviewsMap({});
    return;
  }

  const unsub = onSnapshot(collection(db, "reviews"), (snapshot) => {
    const map = {};

    snapshot.docs.forEach((docSnap) => {
      const review = {
        id: docSnap.id,
        ...docSnap.data(),
      };

      if (!review.jobId || !review.reviewerId) return;

      map[`${review.jobId}_${review.reviewerId}`] = review;
    });

    setReviewsMap(map);
  });

  return () => unsub();
}, [currentUser?.uid]);

  const areaCounts = useMemo(() => {
    const counts = {};

    fields.forEach((job) => {
      if (Array.isArray(job.fields)) {
        job.fields.forEach((field) => {
          if (!field.city) return;
          counts[field.city] = (counts[field.city] || 0) + 1;
        });
      }
    });

    dayJobs.forEach((job) => {
      if (!job.city) return;
      counts[job.city] = (counts[job.city] || 0) + 1;
    });

    return counts;
  }, [fields, dayJobs]);

  const bestDealId = useMemo(() => {
    let bestId = null;
    let bestValue = Infinity;

    fields.forEach((job) => {
      const totalAcres = getTotalAcres(job);
      if (!totalAcres) return;

      if (totalAcres < bestValue) {
        bestValue = totalAcres;
        bestId = job.id;
      }
    });

    return bestId;
  }, [fields]);

  const visibleFieldJobs = useMemo(() => {
    return fields
      .filter((job) => {
        if ((job.status || "open") !== "open") return false;

        const primaryLocation = getJobPrimaryLocation(job);

        if (filterState) {
  const searchText = getJobLocationSearchText(job);
  const filterText = normalizeSearch(filterState);

  if (!searchText.includes(filterText)) return false;
}

        const distance = getSafeDistanceMiles(userLocation, primaryLocation);
        if (distance === null) return true;

        return distance <= radius;
      })
      .sort((a, b) => {
        if (!userLocation) return 0;

        const locA = getJobPrimaryLocation(a);
        const locB = getJobPrimaryLocation(b);

        const distA = getSafeDistanceMiles(userLocation, locA) ?? Infinity;
        const distB = getSafeDistanceMiles(userLocation, locB) ?? Infinity;

        return distA - distB;
      });
  }, [fields, filterState, radius, userLocation]);

  const visibleDayJobs = useMemo(() => {
    return dayJobs
      .filter((job) => {
        if (job.status && job.status !== "open") return false;

        const primaryLocation = getJobPrimaryLocation(job);

        if (filterState) {
  const searchText = getJobLocationSearchText(job);
  const filterText = normalizeSearch(filterState);

  if (!searchText.includes(filterText)) return false;
}

        const distance = getSafeDistanceMiles(userLocation, primaryLocation);
        if (distance === null) return true;

        return distance <= radius;
      })
      .sort((a, b) => {
        if (!userLocation) return 0;

        const locA = getJobPrimaryLocation(a);
        const locB = getJobPrimaryLocation(b);

        const distA = getSafeDistanceMiles(userLocation, locA) ?? Infinity;
        const distB = getSafeDistanceMiles(userLocation, locB) ?? Infinity;

        return distA - distB;
      });
  }, [dayJobs, filterState, radius, userLocation]);

  const visibleProducts = useMemo(() => {
    return products
      .filter((product) => {
        const section = product.marketplaceSection || MARKETPLACE_SECTIONS.GENERAL;
        if (section !== productSection) return false;
        const availability = getProductAvailabilityStatus(product);
        if (availability === PRODUCT_AVAILABILITY.ARCHIVED || product.isArchived) return false;
        if (product.status === "sold") return false;
        if (productAvailabilityFilter === "active_future") {
          if (
            ![
              PRODUCT_AVAILABILITY.AVAILABLE_NOW,
              PRODUCT_AVAILABILITY.COMING_SOON,
              PRODUCT_AVAILABILITY.MADE_TO_ORDER,
            ].includes(availability)
          ) {
            return false;
          }
        } else if (availability !== productAvailabilityFilter) {
          return false;
        }
        if (productSection === MARKETPLACE_SECTIONS.PLANTS_GARDEN) {
          if (product.status && !["active", "out_of_stock"].includes(product.status)) {
            return false;
          }
          if (
            productCategoryFilter &&
            product.plantCategory !== productCategoryFilter
          ) {
            return false;
          }
        }
        if (!isPlantsGardenPage && marketProductTypeFilter) {
          if (getMarketProductCategoryValue(product) !== marketProductTypeFilter) {
            return false;
          }
        }
        const barter = normalizeBarterFields(product);
        if (productExchangeFilter === EXCHANGE_TYPES.CASH_ONLY && barter.exchangeType !== EXCHANGE_TYPES.CASH_ONLY) {
          return false;
        }
        if (productExchangeFilter === EXCHANGE_TYPES.CASH_OR_TRADE && barter.exchangeType !== EXCHANGE_TYPES.CASH_OR_TRADE) {
          return false;
        }
        if (productExchangeFilter === EXCHANGE_TYPES.TRADE_ONLY && barter.exchangeType !== EXCHANGE_TYPES.TRADE_ONLY) {
          return false;
        }
        if (productExchangeFilter === EXCHANGE_TYPES.FREE && barter.exchangeType !== EXCHANGE_TYPES.FREE) {
          return false;
        }
        if (productExchangeFilter === "open_to_trade" && !barter.barterEnabled) {
          return false;
        }

        const loc = getProductLocation(product);

        if (filterState) {
  const searchText = getProductLocationSearchText(product);
  const filterText = normalizeSearch(filterState);

  if (!searchText.includes(filterText)) return false;
}

        return productPassesMarketplaceRadius(marketViewerLocation, loc, radius);
      })
      .sort((a, b) => {
        if (productSort === "price_low") {
          return Number(a.price || 0) - Number(b.price || 0);
        }

        if (productSort === "price_high") {
          return Number(b.price || 0) - Number(a.price || 0);
        }

        if (productSort === "newest") {
          const aTime = a.createdAt?.toMillis?.() || 0;
          const bTime = b.createdAt?.toMillis?.() || 0;
          return bTime - aTime;
        }

        const locA = getProductLocation(a);
        const locB = getProductLocation(b);

        const distA = getMarketProductSortDistance(marketViewerLocation, locA);
        const distB = getMarketProductSortDistance(marketViewerLocation, locB);

        return distA - distB;
      });
  }, [
    products,
    productSection,
    productCategoryFilter,
    marketProductTypeFilter,
    productAvailabilityFilter,
    productExchangeFilter,
    productSort,
    filterState,
    radius,
    marketViewerLocation,
  ]);

  const productMarketDiagnostics = useMemo(() => {
    if (!marketDebugEnabled) return [];

    return products.map((product) => {
      const loc = getProductLocation(product);
      const section = product.marketplaceSection || MARKETPLACE_SECTIONS.GENERAL;
      const availability = getProductAvailabilityStatus(product);
      const searchText = getProductLocationSearchText(product);
      const filterText = normalizeSearch(filterState);
      const computedDistance = getSafeDistanceMiles(marketViewerLocation, loc);
      const broadAreaMatch = isBroadAreaMatch(marketViewerLocation, loc);
      const marketplaceSectionPass = section === productSection;
      const archivePass =
        availability !== PRODUCT_AVAILABILITY.ARCHIVED &&
        product.isArchived !== true &&
        product.status !== "sold";
      const availabilityPass =
        productAvailabilityFilter === "active_future"
          ? [
              PRODUCT_AVAILABILITY.AVAILABLE_NOW,
              PRODUCT_AVAILABILITY.COMING_SOON,
              PRODUCT_AVAILABILITY.MADE_TO_ORDER,
            ].includes(availability)
          : availability === productAvailabilityFilter;
      const plantsCategoryPass =
        productSection !== MARKETPLACE_SECTIONS.PLANTS_GARDEN ||
        !productCategoryFilter ||
        product.plantCategory === productCategoryFilter;
      const marketCategoryValue = getMarketProductCategoryValue(product);
      const marketCategoryPass =
        isPlantsGardenPage ||
        !marketProductTypeFilter ||
        marketCategoryValue === marketProductTypeFilter;
      const barter = normalizeBarterFields(product);
      const exchangePass =
        productExchangeFilter === "all" ||
        (productExchangeFilter === "open_to_trade"
          ? barter.barterEnabled
          : barter.exchangeType === productExchangeFilter);
      const searchPass = !filterText || searchText.includes(filterText);
      const radiusPass = productPassesMarketplaceRadius(marketViewerLocation, loc, radius);

      return {
        id: product.id,
        sellerId: product.userId || product.ownerId || product.sellerId || null,
        title: product.name || product.plantName || "Farm Product",
        marketplaceSection: section,
        productType: product.productType || product.plantCategory || "",
        marketCategory: marketCategoryValue,
        availability,
        status: product.status || "",
        isArchived: product.isArchived === true,
        quantity: Number(product.quantityAvailable ?? product.quantity ?? 1),
        price: Number(product.price || 0),
        priceType: product.priceType || "",
        priceStartsAt: product.priceStartsAt === true,
        locationText: product.locationText || "",
        displayLabel: product.displayLabel || product.label || "",
        city: product.city || "",
        county: product.county || "",
        state: product.state || "",
        rootLat: product.lat ?? null,
        rootLng: product.lng ?? null,
        nestedLocation: product.location || null,
        source: product.source || "",
        locationSource: product.locationSource || "",
        locationMissing: product.locationMissing === true,
        normalizedLocation: getLocationDiagnostics({
          id: product.id,
          type: "products",
          locationData: product,
          comparisonLocation: marketViewerLocation,
        }),
        viewer: marketViewerLocation || null,
        computedDistance,
        broadAreaMatch,
        selectedRadius: radius,
        filters: {
          marketplaceSection: marketplaceSectionPass,
          archive: archivePass,
          availability: availabilityPass,
          quantity: true,
          search: searchPass,
          radius: radiusPass,
          locationValidity: computedDistance !== null || broadAreaMatch,
          plantsCategory: plantsCategoryPass,
          marketCategory: marketCategoryPass,
          exchange: exchangePass,
        },
        visible:
          marketplaceSectionPass &&
          archivePass &&
          availabilityPass &&
          plantsCategoryPass &&
          marketCategoryPass &&
          exchangePass &&
          searchPass &&
          radiusPass,
      };
    });
  }, [
    filterState,
    isPlantsGardenPage,
    marketProductTypeFilter,
    marketDebugEnabled,
    marketViewerLocation,
    productAvailabilityFilter,
    productExchangeFilter,
    productCategoryFilter,
    productSection,
    products,
    radius,
  ]);

 useEffect(() => {
  setDashboardView(initialDashboardView);
}, [initialDashboardView]);

 useEffect(() => {
  if (
    requestedDashboardView === "worker" ||
    requestedDashboardView === "owner" ||
    requestedDashboardView === "market" ||
    requestedDashboardView === "events" ||
    requestedDashboardView === "planner"
  ) {
    setDashboardView(requestedDashboardView);
  }
}, [requestedDashboardView]);

useEffect(() => {
  if (requestedRadius) {
    setRadius(Number(requestedRadius));
  }
}, [requestedRadius]);

useEffect(() => {
  if (!focusProductId) {
    if (openedFocusProductId) setOpenedFocusProductId(null);
    return;
  }

  const focusedProduct = visibleProducts.find(
    (product) => product.id === focusProductId
  );

  if (focusedProduct) {
    if (openedFocusProductId !== focusProductId) {
      setSelectedProduct(focusedProduct);
      setRequestQuantity(1);
      setOpenedFocusProductId(focusProductId);
    }

    return;
  }

  const el = document.getElementById(`product-${focusProductId}`);

  if (el) {
    setTimeout(() => {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 100);
  }
}, [focusProductId, openedFocusProductId, visibleProducts]);

  const combinedVisibleJobs = useMemo(() => {
  if (dashboardView === "owner") {
    return [...fields, ...dayJobs].filter(
      (job) => job.ownerId === currentUser?.uid && !job.isArchived
    );
  }

  const openWorkerJobs = [...visibleFieldJobs, ...visibleDayJobs].filter(
    (job) => job.ownerId !== currentUser?.uid
  );

  const myCompletedWorkerJobs = [...fields, ...dayJobs].filter((job) => {
    if (!currentUser?.uid || job.isArchived) return false;
    if ((job.status || "open") !== "completed") return false;

    const workerId =
      job.acceptedBy ||
      job.acceptedOperator ||
      job.completedBy ||
      job.acceptedApplicantId;

    return workerId === currentUser.uid;
  });

  const merged = [...openWorkerJobs, ...myCompletedWorkerJobs];

  const deduped = Array.from(
    new Map(
      merged.map((job) => [
        `${job.isDayJob ? "day" : "field"}-${job.id}`,
        job,
      ])
    ).values()
  );

  return deduped.sort((a, b) => {
    if (!userLocation) return 0;

    const locA = getJobPrimaryLocation(a);
    const locB = getJobPrimaryLocation(b);

    const distA = getSafeDistanceMiles(userLocation, locA) ?? Infinity;
    const distB = getSafeDistanceMiles(userLocation, locB) ?? Infinity;

    return distA - distB;
  });
}, [
  fields,
  dayJobs,
  visibleFieldJobs,
  visibleDayJobs,
  userLocation,
  dashboardView,
  currentUser?.uid,
]);

  const groupedJobs = useMemo(() => {
    const groups = {
      open: [],
      in_progress: [],
      completed: [],
    };

    combinedVisibleJobs.forEach((job) => {
      const status = job.status || "open";
      if (groups[status]) groups[status].push(job);
    });

    return groups;
  }, [combinedVisibleJobs]);

  useEffect(() => {
    if (!focusJobId) return;

    const el = document.getElementById(`job-${focusJobId}`);
    if (el) {
      setTimeout(() => {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 100);
    }
  }, [focusJobId, combinedVisibleJobs]);

  const myPlannerEvents = events.filter((event) => {
  const ownerId = event.ownerId || event.userId || event.hostId;
  return ownerId === currentUser?.uid && event.isArchived !== true;
});

const activePlannerReservations = eventReservations.filter((r) =>
  ["pending", "pending_payment", "approved", "accepted"].includes(
    r.status || "pending"
  )
);

const pageTitle =
  isPlantsGardenPage
    ? "Plants & Garden"
    : dashboardView === "owner"
    ? "My Jobs Dashboard"
    : dashboardView === "market"
    ? "Local Farm Market"
    : dashboardView === "events"
    ? "Community Events"
    : dashboardView === "planner"
    ? "Event Planner Dashboard"
    : "Available Jobs";

const pageSubtitle =
  isPlantsGardenPage
    ? "Buy and sell starts, plants, shrubs, trees, seeds, bulbs, and garden supplies locally."
    : dashboardView === "owner"
    ? "Manage your jobs, bids, progress, and conversations."
    : dashboardView === "market"
    ? "Find fresh goods, handmade items, farm supplies, plants, and local offers near you."
    : dashboardView === "events"
    ? "Find farm events, fairs, vendor spots, and local gatherings near you."
    : dashboardView === "planner"
    ? "Manage your events, vendor reservations, booth spots, and payment confirmations."
    : "Browse open jobs, check locations, and message owners without leaving the page.";

const countLabel =
  isPlantsGardenPage
    ? "Plant Listings"
    : dashboardView === "owner"
    ? "My Jobs"
    : dashboardView === "market"
    ? "Products"
    : dashboardView === "events"
    ? "Events"
    : dashboardView === "planner"
    ? "My Events"
    : "Open Jobs";

const countValue =
  isPlantsGardenPage
    ? visibleProducts.length
    : dashboardView === "market"
    ? visibleProducts.length
    : dashboardView === "events"
    ? events.length
    : dashboardView === "planner"
    ? myPlannerEvents.length
    : combinedVisibleJobs.length;

const homeQuickActions = [
  {
    title: "Find Work",
    subtitle: "Open jobs",
    icon: "🛠",
    action: () => setDashboardView("worker"),
  },
  {
    title: "Post Job",
    subtitle: "Map work",
    icon: "🌾",
    action: () => {
      if (requireSignIn("Create a free account or sign in to post a job.")) {
        navigate("/map");
      }
    },
  },
  {
    title: "Browse Products",
    subtitle: "Local goods",
    icon: "🛒",
    action: () => setDashboardView("market"),
  },
  {
    title: "Ask Community",
    subtitle: "Local advice",
    icon: "💬",
    action: () => {
      if (requireSignIn("Create a free account or sign in to post in the community.")) {
        navigate("/community");
      }
    },
  },
];

const shouldShowHomePanels = !isPlantsGardenPage && dashboardView !== "market";

const todayOnFarmPreview = useMemo(() => {
  const today = new Date();
  const month = today.getMonth();
  const current = homeWeatherState.data?.current || {};
  const daily = homeWeatherState.data?.daily || {};
  const currentTemp = Number(current.temperature_2m);
  const dailyLow = Number(daily.temperature_2m_min?.[0]);
  const weatherCode = Number(current.weather_code);
  const frostRisk = getHomeFrostRisk(month, currentTemp, dailyLow);
  const fieldCondition = getHomeFieldCondition(
    daily.precipitation_sum?.[0],
    current.precipitation,
    current.rain
  );
  const weather =
    !homeWeatherState.error && Number.isFinite(currentTemp)
      ? `${Math.round(currentTemp)}°F • ${getHomeWeatherCodeLabel(weatherCode)}`
      : homeWeatherState.loading
      ? "Loading local weather"
      : "Check today’s forecast";
  const seasonalNotes = [
    "Plan winter chores and check stored feed.",
    "Prep soil tests and early spring jobs.",
    "Watch wet fields before heavy equipment work.",
    "Keep summer watering and shade plans handy.",
    "List seasonal produce and weekend help early.",
    "Review frost windows before planting tender crops.",
  ];
  const seasonalIndex =
    month <= 1 || month === 11
      ? 0
      : month <= 2
      ? 1
      : month <= 4
      ? 2
      : month <= 7
      ? 3
      : month <= 8
      ? 4
      : 5;
  const moonPhase = getHomeMoonPhase(today);
  const seasonalNote =
    frostRisk === "High" || frostRisk === "Watch"
      ? "Check frost-sensitive plants tonight."
      : fieldCondition === "Wet/Muddy"
      ? "Watch wet fields before heavy equipment work."
      : fieldCondition === "Damp"
      ? "Let low spots firm up before heavy traffic."
      : !homeWeatherState.error
      ? "Good window for warm-season crop care."
      : seasonalNotes[seasonalIndex] || "Open the Almanac for today’s local farm notes.";

  return {
    weather,
    fieldCondition: homeWeatherState.error ? "View local conditions" : fieldCondition,
    frostRisk: `${frostRisk} frost risk`,
    moonPhase,
    seasonalNote,
  };
}, [homeWeatherState.data, homeWeatherState.error, homeWeatherState.loading]);

  return (
    <div
      className={`card section page-shell page-shell-wide${
        dashboardView === "market" || isPlantsGardenPage ? " marketplace-page-shell" : ""
      }`}
    >
      <div className={`page-topbar${dashboardView === "market" ? " marketplace-topbar" : ""}`}>
        <div>
          <h2>{pageTitle}</h2>
          <p className="page-subtitle">{pageSubtitle}</p>
        </div>

        <div className="market-header-side">
          <div className="map-stat-pills">
            <span className="map-stat-pill">
              {countLabel}: <strong>{countValue}</strong>
            </span>
            <span className="map-stat-pill">
              Radius: <strong>{radius} mi</strong>
            </span>
          </div>

          {(dashboardView === "market" || isPlantsGardenPage) && (
            <div className="market-header-actions">
              <button
                type="button"
                className="btn-primary"
                onClick={() => {
                  if (requireSignIn("Create a free account or sign in to add a listing.")) {
                    navigate(isPlantsGardenPage ? "/plants-garden/new" : "/add-product");
                  }
                }}
              >
                Add Listing
              </button>
              {!isPlantsGardenPage && (
                <button
                  type="button"
                  className="btn-soft"
                  onClick={() => {
                    if (requireSignIn("Create a free account or sign in to post a want-ad.")) {
                      navigate("/community", {
                        state: { typeFilter: "want_ad", openPostForm: true },
                      });
                    }
                  }}
                >
                  Looking For / Want-Ad
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {shouldShowHomePanels && (
        <>
      <section className="home-hub-section" aria-label="Quick actions">
        <div className="home-hub-heading">
          <div>
            <h3>What do you need today?</h3>
          </div>
        </div>

        <div className="home-hub-grid">
          {homeQuickActions.map((item) => (
            <button
              type="button"
              className="home-hub-card"
              key={item.title}
              onClick={item.action}
            >
              <span>{item.icon}</span>
              <strong>{item.title}</strong>
              <small>{item.subtitle}</small>
            </button>
          ))}
        </div>
      </section>

      <div className="daily-farm-preview-card">
        <div className="daily-farm-preview-main">
          <div className="almanac-preview-icon" aria-hidden="true">
            🌦
          </div>
          <div className="almanac-preview-copy">
            <h3>Today on the Farm</h3>
            <p>
              A quick look at weather, field conditions, frost risk, and seasonal
              notes for your area.
            </p>
          </div>
        </div>

        <div className="daily-farm-preview-grid">
          <span>
            <strong>Weather</strong>
            {todayOnFarmPreview.weather}
          </span>
          <span>
            <strong>Field</strong>
            {todayOnFarmPreview.fieldCondition}
          </span>
          <span>
            <strong>Frost</strong>
            {todayOnFarmPreview.frostRisk}
          </span>
          <span>
            <strong>Moon</strong>
            {todayOnFarmPreview.moonPhase}
          </span>
        </div>

        <p className="daily-farm-preview-note">{todayOnFarmPreview.seasonalNote}</p>

        <button
          type="button"
          className="btn-primary"
          onClick={() => navigate("/almanac")}
        >
          View Almanac
        </button>
      </div>
        </>
      )}

      <div
        className={`job-feed-controls card-lite${
          dashboardView === "market" || isPlantsGardenPage ? " marketplace-filter-bar" : ""
        }`}
      >
        <div className="filter-block">
          <label className="form-label" htmlFor="job-feed-location-filter">
  {isPlantsGardenPage || dashboardView === "market"
    ? "Search listings, seller, or location"
    : "Filter by City / County / State"}
</label>

<input
  id="job-feed-location-filter"
  name="jobFeedLocationFilter"
  type="text"
  placeholder={
    isPlantsGardenPage
      ? "Example: tomato, seeds, Tullahoma, or TN"
      : "Example: Tullahoma, Bedford, or TN"
  }
  value={filterState}
  onChange={(e) => setFilterState(e.target.value)}
/>
        </div>

        <div className="filter-block">
          <label className="form-label" htmlFor="job-feed-radius">
  Radius: {radius} miles
</label>

<input
  id="job-feed-radius"
  name="jobFeedRadius"
  type="range"
  min="5"
  max="100"
  value={radius}
  onChange={(e) => setRadius(Number(e.target.value))}
/>
<button
  type="button"
  className="btn-soft location-change-link"
  onClick={() => {
    if (requireSignIn("Create a free account or sign in to save your location.")) {
      navigate("/edit-profile#profile-location-section");
    }
  }}
>
  Change location
</button>
        </div>
        {(dashboardView === "market" || isPlantsGardenPage) && (
          <div className="filter-block">
            <label className="form-label" htmlFor="product-availability-filter">
              Availability
            </label>
            <select
              id="product-availability-filter"
              value={productAvailabilityFilter}
              onChange={(e) => setProductAvailabilityFilter(e.target.value)}
            >
              {PRODUCT_AVAILABILITY_FILTERS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.value === "active_future" ? "Available + Future" : option.label}
                </option>
              ))}
            </select>
          </div>
        )}
        {(dashboardView === "market" || isPlantsGardenPage) && (
          <div className="filter-block">
            <label className="form-label" htmlFor="product-exchange-filter">
              Exchange
            </label>
            <select
              id="product-exchange-filter"
              value={productExchangeFilter}
              onChange={(e) => setProductExchangeFilter(e.target.value)}
            >
              <option value="all">All</option>
              <option value={EXCHANGE_TYPES.CASH_ONLY}>Cash</option>
              <option value={EXCHANGE_TYPES.CASH_OR_TRADE}>Cash or trade</option>
              <option value={EXCHANGE_TYPES.TRADE_ONLY}>Trade only</option>
              <option value={EXCHANGE_TYPES.FREE}>Free</option>
              <option value="open_to_trade">Open to trade</option>
            </select>
          </div>
        )}
      </div>
      {dashboardView === "market" && !isPlantsGardenPage && (
        <div className="market-category-chip-row" aria-label="Marketplace categories">
          {MARKET_PRODUCT_CATEGORY_FILTERS.map((item) => (
            <button
              key={item.value || "all"}
              type="button"
              className={marketProductTypeFilter === item.value ? "active" : ""}
              onClick={() => {
                if (item.value === "plants") {
                  navigate("/plants-garden");
                  return;
                }

                setMarketProductTypeFilter(item.value);
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
      {isPlantsGardenPage && (
        <div className="plants-market-toolbar card-lite">
          <div className="filter-block">
            <label className="form-label" htmlFor="plants-category-filter">
              Category
            </label>
            <select
              id="plants-category-filter"
              value={productCategoryFilter}
              onChange={(e) => setProductCategoryFilter(e.target.value)}
            >
              <option value="">All plant categories</option>
              {PLANT_CATEGORY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-block">
            <label className="form-label" htmlFor="plants-sort">
              Sort
            </label>
            <select
              id="plants-sort"
              value={productSort}
              onChange={(e) => setProductSort(e.target.value)}
            >
              <option value="nearest">Nearest</option>
              <option value="newest">Newest</option>
              <option value="price_low">Price low-high</option>
              <option value="price_high">Price high-low</option>
            </select>
          </div>

          <div className="plants-toolbar-action">
            <button
              type="button"
              className="btn-primary"
              onClick={() => {
                if (requireSignIn("Create a free account or sign in to add a plant listing.")) {
                  navigate("/plants-garden/new");
                }
              }}
            >
              Add Plant Listing
            </button>
          </div>
        </div>
      )}
      {marketDebugEnabled && (dashboardView === "market" || isPlantsGardenPage) && (
        <pre className="market-debug-panel">
          {JSON.stringify(
            {
              viewer: marketViewerLocation || null,
              productSection,
              selectedRadius: radius,
              productAvailabilityFilter,
              productCategoryFilter,
              productCount: products.length,
              visibleProductCount: visibleProducts.length,
              products: productMarketDiagnostics,
            },
            null,
            2
          )}
        </pre>
      )}
{dashboardView === "market" ? (
  visibleProducts.length === 0 ? (
    <div className="empty-state">
      <h3>
        {isPlantsGardenPage
          ? "No local plant listings right now"
          : "🛒 No local products right now"}
      </h3>
      <p>
        {isPlantsGardenPage
          ? "Check back soon, widen your radius, or list starts, seeds, plants, or garden supplies for neighbors to find."
          : "No local listings yet. Widen your radius, check back soon, or be the first to add one."}
      </p>
      <div className="empty-state-actions">
        <button
          type="button"
          className="btn-primary"
          onClick={() => {
            if (requireSignIn("Create a free account or sign in to add a listing.")) {
              navigate(isPlantsGardenPage ? "/plants-garden/new" : "/add-product");
            }
          }}
        >
          {isPlantsGardenPage ? "Add Plant Listing" : "Add Product"}
        </button>
        {!isPlantsGardenPage && (
          <button type="button" className="btn-soft" onClick={() => setDashboardView("events")}>
            View Events
          </button>
        )}
      </div>
    </div>
  ) : (
    <div className="job-grid marketplace-product-grid">
      {visibleProducts.map((product) => {
  const loc = getProductLocation(product);
  const linkedStand = getMarketplaceProductLinkedStand(product, marketplaceFarmStands);
  const locationLabel = getMarketplaceProductDisplayLocationLabel(product, linkedStand);
  const productDistanceMiles = getSafeDistanceMiles(marketViewerLocation, loc);
  const reviewStats = getProductReviewStats(product.id);
  const sellerId = product.userId || product.ownerId || product.sellerId;
  const seller = usersMap[sellerId] || {};
  const publicSellerName =
    product.sellerName ||
    product.sellerFarmName ||
    product.sellerBusinessName ||
    product.farmName ||
    product.businessName ||
    product.standName ||
    "Local seller";
  const availabilityStatus = getProductAvailabilityStatus(product);
  const isComingSoon = availabilityStatus === PRODUCT_AVAILABILITY.COMING_SOON;

  return (
          <div
            key={product.id}
            id={`product-${product.id}`}
            className="card job-card product-card market-product-card"
            onClick={() => {
  trackPublicOpen("public_product_open", "marketplace", product.id);
  setSelectedProduct(product);
  setRequestQuantity(1);
}}
            style={{ cursor: "pointer" }}
            >



            {product.image && (
              <img
                src={product.image}
                alt={product.name || "Product"}
                className="product-card-image"
                loading="lazy"
              />
            )}

            {product.status === "out_of_stock" && (
  <span className="hot-badge">🚫 Out of Stock</span>
)}

{product.status === "sold" && (
  <span className="acre-badge">✅ Sold</span>
)}

{(product.userId || product.ownerId || product.sellerId) !== currentUser?.uid && (
  <div
    className="feed-report-row"
    onClick={(e) => e.stopPropagation()}
  >
    <ReportButton
      type="product"
      targetId={product.id}
      targetOwnerId={product.userId || product.ownerId || product.sellerId || null}
      targetTitle={product.name || "Farm Product"}
      buttonLabel="Report"
      compact
    />
  </div>
)}

            <div className="job-card-header">
              <div>
                <div className="job-card-title">
                  {product.name || "Farm Product"}
                </div>

                <div className="job-card-subtitle">
  {locationLabel}
</div>
<div className="product-market-rating">
  {reviewStats.count > 0 ? (
    <>
      ⭐ <strong>{reviewStats.average}</strong>{" "}
      <span>
        ({reviewStats.count} review{reviewStats.count === 1 ? "" : "s"})
      </span>
    </>
  ) : (
    <span>New listing</span>
  )}
</div>
{(sellerId || publicSellerName) && (currentUser?.uid ? (
  <button
    type="button"
    className="product-seller-link"
    onClick={(event) => {
      event.stopPropagation();
      navigate(getProfilePath({ id: sellerId, uid: sellerId, ...seller }));
    }}
  >
    Seller: {seller.name || seller.displayName || publicSellerName}
  </button>
) : (
  <span className="product-seller-link product-seller-link-static">
    Seller: {publicSellerName}
  </span>
))}
              </div>
            </div>

            <div className="job-meta">
              <div className="availability-detail-strip">
                <span className={`availability-badge availability-${availabilityStatus}`}>
                  {getProductAvailabilityLabel(availabilityStatus)}
                </span>
                {isComingSoon && product.availabilityStart && (
                  <span>Expected {product.availabilityStart}</span>
                )}
                {isComingSoon && product.availabilityNote && (
                  <span>{product.availabilityNote}</span>
                )}
              </div>
              <p className="price">
                {getMarketProductPriceLabel(product)}
              </p>
              <div className="exchange-badge-row">
                {getExchangeBadges(product).map((badge) => (
                  <span className="exchange-badge" key={badge}>
                    {badge}
                  </span>
                ))}
              </div>
              <p className="market-availability-line">
                <strong>Available:</strong>
                <span className="market-availability-value">
                  {formatAvailability(
                    product.quantityAvailable ?? product.quantity ?? 1,
                    product.unit
                  )}
                </span>
              </p>

              {isPlantsGardenPage && (
                <div className="plant-market-details">
                  <span>{getPlantCategoryLabel(product.plantCategory)}</span>
                  {product.variety && <span>Variety: {product.variety}</span>}
                  {product.potSize && <span>{product.potSize}</span>}
                  {product.plantSize && <span>{product.plantSize}</span>}
                  {product.annualOrPerennial && (
                    <span>{getPlantLifeCycleLabel(product.annualOrPerennial)}</span>
                  )}
                  {product.sunNeeds && <span>Sun: {getSunNeedsLabel(product.sunNeeds)}</span>}
                  {product.deliveryAvailable && <span>Delivery available</span>}
                </div>
              )}

              {product.description && <p className="market-product-description-snippet">{product.description}</p>}
               
              {product.productType === "hay_straw" && product.hayDetails && (
  <div className="hay-market-details">
    <span>
      🌾 {product.hayDetails.hayType?.replace("_", " ") || "Hay"}
    </span>

    <span>
      {product.hayDetails.baleType?.replace("_", " ") || "Bale"}
    </span>

    {product.hayDetails.cutting && (
      <span>{product.hayDetails.cutting}</span>
    )}

    {product.hayDetails.baleWeight && (
      <span>{product.hayDetails.baleWeight}</span>
    )}

    {product.hayDetails.storedInside && <span>Stored inside</span>}

    {product.hayDetails.deliveryAvailable && (
  <span>
    Delivery within{" "}
    {Number(product.hayDetails.deliveryRadiusMiles || 0) || "?"} miles
  </span>
)}
  </div>
)}

              {productDistanceMiles !== null && (
                <p className="market-distance-line">
                  📍{" "}
                  {productDistanceMiles.toFixed(1)}{" "}
                  miles away
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  )

  ) : dashboardView === "planner" ? (
  !canViewAsEventPlanner ? (
    <div className="empty-state">
      <h3>🎪 Event Planner access only</h3>
      <p>This dashboard is only available for event planners.</p>
    </div>
  ) : (
    <div className="event-planner-dashboard">
      <div className="event-planner-grid">
        <div className="event-planner-stat-card">
          <span>📅</span>
          <strong>{myPlannerEvents.length}</strong>
          <small>My Events</small>
        </div>

        <div className="event-planner-stat-card">
          <span>🎪</span>
          <strong>{activePlannerReservations.length}</strong>
          <small>Active Reservations</small>
        </div>

        <div className="event-planner-stat-card">
          <span>✅</span>
          <strong>
            {
              eventReservations.filter((r) => r.status === "completed")
                .length
            }
          </strong>
          <small>Paid / Completed</small>
        </div>
      </div>

      <div className="profile-card">
        <div className="profile-products-header">
          <h3>📅 My Events</h3>

          <button
            className="btn-primary"
            type="button"
            onClick={() => {
              if (requireSignIn("Create a free account or sign in to add an event.")) {
                navigate("/add-event");
              }
            }}
          >
            ➕ Add Event
          </button>
        </div>

        {myPlannerEvents.length === 0 ? (
          <div className="profile-products-empty">
            You have not posted any events yet.
          </div>
        ) : (
          <div className="event-planner-list">
            {myPlannerEvents.map((event) => (
              <div key={event.id} className="event-planner-card">
                {event.image && (
                  <img
                    src={event.image}
                    alt={event.title || "Event"}
                    className="event-planner-img"
                  />
                )}

                <div className="event-planner-body">
                  <h4>{event.title || "Community Event"}</h4>

                  <p>📅 {event.eventDate || "Date TBD"}</p>
                  <p>📍 {event.locationText || "Location not listed"}</p>

                  {event.description && <p>{event.description}</p>}

                  <div className="event-planner-actions">
                    <button
                      className="btn-soft"
                      type="button"
                      onClick={() => {
                        trackPublicOpen("public_event_open", "events", event.id);
                        setSelectedEvent(event);
                      }}
                    >
                      View
                    </button>

                    <button
                      className="btn-soft"
                      type="button"
                      onClick={() => quickUpdateEvent(event)}
                    >
                      ✏️ Update
                    </button>

                    <button
                      className="btn-danger-soft"
                      type="button"
                      onClick={() => archiveEvent(event)}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="profile-card">
        <h3>🎪 Reservation Requests</h3>

        {activePlannerReservations.length === 0 ? (
          <div className="profile-products-empty">
            No active reservation requests right now.
          </div>
        ) : (
          <div className="event-planner-list">
            {activePlannerReservations.map((r) => (
              <div key={r.id} className="field-card">
                <p>
                  <strong>{r.requesterName || "Vendor"}</strong>
                </p>

                <p>
                  <strong>Event:</strong>{" "}
                  {r.eventTitle || "Community Event"}
                </p>

                <p>
                  <strong>Booth:</strong>{" "}
                  {r.boothCategoryLabel ||
                    r.boothType ||
                    r.boothCategory ||
                    "Vendor Booth"}
                </p>

                {r.note && <p>{r.note}</p>}

                <p>
                  <strong>Status:</strong>{" "}
                  {r.status === "accepted"
                    ? "✅ Accepted - Pay in person to complete"
                    : "⏳ Pending approval"}
                </p>

                <div className="field-card-actions">
                  {["pending", "pending_payment", "approved"].includes(
                    r.status || "pending"
                  ) && (
                    <>
                      <button
                        className="btn-primary"
                        type="button"
                        onClick={() => acceptEventReservationRequest(r)}
                      >
                        Accept - Pay In Person
                      </button>

                      <button
                        className="btn-danger-soft"
                        type="button"
                        onClick={() => declineEventReservation(r)}
                      >
                        Decline
                      </button>
                    </>
                  )}

                  {r.status === "accepted" && (
                    <button
                      className="btn-primary"
                      type="button"
                      onClick={() => confirmPaidEventReservation(r)}
                    >
                      Confirm Paid In Person
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )

) : dashboardView === "events" ? (
  // 📅 EVENTS (THIS IS NEW CORRECT LOCATION)
  events.length === 0 ? (
    <div className="empty-state">
      <h3>📅 No events yet</h3>
      <p>Be the first to post a market, class, vendor spot, or farm gathering.</p>
      <div className="empty-state-actions">
        <button
          type="button"
          className="btn-primary"
          onClick={() => {
            if (requireSignIn("Create a free account or sign in to add an event.")) {
              navigate("/add-event");
            }
          }}
        >
          Add Event
        </button>
        <button
          type="button"
          className="btn-soft"
          onClick={() => {
            if (requireSignIn("Create a free account or sign in to post in the community.")) {
              navigate("/community");
            }
          }}
        >
          Ask Community
        </button>
      </div>
    </div>
  ) : (
    <div className="job-grid">
  {dashboardView === "events" && (
  <div className="calendar-container">

    <div className="calendar-header">
      <button
        onClick={() =>
          setCurrentMonth(
            new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1)
          )
        }
      >
        ←
      </button>

      <h3>
        {currentMonth.toLocaleString("default", {
          month: "long",
          year: "numeric",
        })}
      </h3>

      <button
        onClick={() =>
          setCurrentMonth(
            new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1)
          )
        }
      >
        →
      </button>
    </div>

    <div className="events-mobile-list" aria-label="Upcoming events">
      <div className="events-mobile-list-heading">
        <strong>Upcoming events</strong>
        <span>{events.length} public events</span>
      </div>

      {events
        .slice()
        .sort((a, b) => String(a.eventDate || "").localeCompare(String(b.eventDate || "")))
        .map((event) => (
          <button
            key={event.id}
            type="button"
            className="events-mobile-card"
            onClick={() => {
              trackPublicOpen("public_event_open", "events", event.id);
              setSelectedEvent(event);
            }}
          >
            <span className="events-mobile-card-date">{formatEventDateRange(event)}</span>
            <strong>{event.title || "Community Event"}</strong>
            <span>{event.locationText || "Location not listed"}</span>
          </button>
        ))}
    </div>

    <div className="calendar-grid">
      {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => (
        <div key={d} className="calendar-day-header">{d}</div>
      ))}

      {getDaysInMonth(currentMonth).map((day, i) => {
        if (!day) return <div key={i} className="calendar-cell empty" />;

        const dateKey = day.toISOString().split("T")[0];
        const dayEvents = events.filter(e => e.eventDate === dateKey);

        return (
          <div key={i} className="calendar-cell">
            <div className="calendar-date">{day.getDate()}</div>

            {dayEvents.map((event) => (
              <div
                key={event.id}
                className="calendar-event"
                onClick={() => {
                  trackPublicOpen("public_event_open", "events", event.id);
                  setSelectedEvent(event);
                }}
              >
                {event.title}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  </div>
)}

{reservationEvent && (
  <div className="modal" onClick={() => setReservationEvent(null)}>
    <div
      className="product-detail-modal"
      onClick={(e) => e.stopPropagation()}
    >
      <h2>Reserve Vendor Spot</h2>

      <p>
        <strong>{reservationEvent.title || "Community Event"}</strong>
      </p>

      <p>📅 {reservationEvent.eventDate || "Date TBD"}</p>
      <p>📍 {reservationEvent.locationText || "Location not set"}</p>

      <div className="summary-box" style={{ marginTop: "14px" }}>
        <strong>Choose booth type</strong>

        {(reservationEvent.boothCategories || []).map((cat) => {
          const remaining = getCategoryRemaining(cat);
          const isFull = remaining <= 0;

          return (
            <label
              key={cat.id}
              className={`field-card ${isFull ? "completed" : ""}`}
              style={{
                display: "block",
                marginTop: "10px",
                cursor: isFull ? "not-allowed" : "pointer",
              }}
            >
              <input
                type="radio"
                id={`booth-category-${cat.id}`}
                name="boothCategory"
                value={cat.id}
                checked={reservationCategoryId === cat.id}
                disabled={isFull}
                onChange={(e) => setReservationCategoryId(e.target.value)}
                style={{ marginRight: "8px" }}
              />

              <strong>{cat.label}</strong>

              <p style={{ margin: "6px 0 0" }}>
                {remaining} / {Number(cat.spotsAvailable || 0)} spots left
              </p>

              {isFull && (
                <span className="acre-badge">Full</span>
              )}
            </label>
          );
        })}
      </div>

      <div style={{ marginTop: "14px" }}>
        <label className="form-label" htmlFor="reservation-note">
  Note to host
</label>

<textarea
  id="reservation-note"
  name="reservationNote"
  value={reservationNote}
  onChange={(e) => setReservationNote(e.target.value)}
  placeholder="Example: I sell honey, jams, and handmade soaps."
  rows="4"
/>
      </div>

      <div className="button-group">
        <button
          className="btn-soft"
          type="button"
          onClick={() => setReservationEvent(null)}
        >
          Cancel
        </button>

        <button
          className="btn-primary"
          type="button"
          disabled={!reservationCategoryId || submittingReservation}
          onClick={submitEventReservation}
        >
          {submittingReservation ? "Sending..." : "Send Request"}
        </button>
      </div>
    </div>
  </div>
)}



</div>
  )
) : combinedVisibleJobs.length === 0 ? (
      
        <div className="empty-state">
          <h3>🌾 Nothing open right now</h3>
          <p>New jobs pop up fast. Try expanding your search or check back soon.</p>

          <button
            className="btn-primary"
            onClick={() => setRadius(100)}
            style={{ marginTop: "10px" }}
          >
            Expand Search
          </button>
          <div className="empty-state-actions">
            <button
              type="button"
              className="btn-soft"
              onClick={() => {
                if (requireSignIn("Create a free account or sign in to post a job.")) {
                  navigate("/map");
                }
              }}
            >
              Post a Job
            </button>
            <button type="button" className="btn-soft" onClick={() => setDashboardView("market")}>
              Browse Products
            </button>
          </div>
        </div>
      ) : (


        
        <div className="job-grid">
          {["open", "in_progress", "completed"].map((status) => {
            const jobs = groupedJobs[status];
            if (!jobs || jobs.length === 0) return null;

            return (
              <div key={status} style={{ width: "100%" }}>
                <h3 style={{ margin: "20px 0 10px" }}>
                  {status === "open" && "🟢 Open Jobs"}
                  {status === "in_progress" && "🚜 In Progress"}
                  {status === "completed" && "✅ Completed"}
                </h3>

                {jobs.map((job) => {
                  const primaryLocation = getJobPrimaryLocation(job);
                  const distance = getSafeDistanceMiles(userLocation, primaryLocation);

                  const totalAcres = getTotalAcres(job);
                  const owner = usersMap[job.ownerId];

                  return (
                    <div
                      key={`${job.isDayJob ? "day" : "field"}-${job.id}`}
                      id={`job-${job.id}`}
                      
                      
                      className={`card job-card ${

                        (job.status || "open") === "open" && job.id === bestDealId ? "featured" : ""
} ${
                        
                        job.status === "completed" ? "completed" : ""
                      }`}
                    >
                      <div className="job-card-header">
                        <div>
                          {job.isDayJob && (
                            <div className="job-card-subtitle">🛠 Day Job</div>
                          )}

                          <div className="job-card-title">
  {job.isDayJob
    ? job.title || "Day Job"
    : `${totalAcres.toFixed(2)} acres • ${
        job.grassType || job.fields?.[0]?.category || "Field Job"
      }`}
</div>

<div className="job-card-subtitle">
  {job.isDayJob
    ? formatLocation(primaryLocation.city, primaryLocation.state)
    : getFieldJobDisplayLocation(job)}
</div>
                        </div>

                        <div className="job-badge-stack">
                          {!job.isDayJob &&
  (job.status || "open") === "open" &&
  job.id === bestDealId && (
    <span className="acre-badge">💰 Best Deal</span>
  )}

                          {primaryLocation.city &&
                            areaCounts[primaryLocation.city] >= 3 && (
                              <span className="hot-badge">🔥 Hot Area</span>
                            )}
                        </div>
                      </div>

                      <div className="job-meta">
                        {job.isDayJob ? (
  <>
    <p>
      <strong>Category:</strong> {job.category || "general"}
    </p>

    <p>
      <strong>Pay:</strong>{" "}
      {job.payType === "quote"
        ? "Open to offers"
        : job.payAmount
        ? `$${job.payAmount}${job.payType === "hourly" ? "/hr" : ""}`
        : "Not listed"}
    </p>

    {job.description && <p>{job.description}</p>}
  </>
) : (
  <>
    <p>
      <strong>Crop:</strong>{" "}
      {job.grassType || job.fields?.[0]?.category || "Field work"}
    </p>

    <p>
      <strong>Total Acres:</strong> {totalAcres.toFixed(2)}
    </p>

    <p>
      <strong>Bid Type:</strong> {formatFieldDealLabel()}
    </p>
  </>
)}

                        {currentUser?.uid ? (
                          <p
                            className="job-owner-link"
                            onClick={() =>
                              navigate(getProfilePath({ id: job.ownerId, uid: job.ownerId, ...(owner || {}) }))
                            }
                          >
                            <strong>Owner:</strong> {owner?.name || "Loading..."}
                          </p>
                        ) : (
                          <p className="job-owner-link job-owner-link-static">
                            <strong>Owner:</strong>{" "}
                            {job.ownerName ||
                              job.farmName ||
                              job.businessName ||
                              job.postedByName ||
                              "Local farm / landowner"}
                          </p>
                        )}

                        {typeof owner?.rating === "number" && (
                          <p>
                            <strong>Rating:</strong> {owner.rating.toFixed(1)}{" "}
                            {owner.reviewCount ? `(${owner.reviewCount} reviews)` : ""}
                          </p>
                        )}

                        <p
                          className="job-link"
                          onClick={() =>
                            navigate("/map", {
                              state: job.isDayJob
                                ? { focusItemId: job.id, focusItemType: "dayJob" }
                                : { focusJobId: job.id },
                            })
                          }
                        >
                          <strong>View on map:</strong>{" "}
                          {primaryLocation.city || ""}
                          {primaryLocation.city && primaryLocation.state ? ", " : ""}
                          {primaryLocation.state || ""}
                        </p>

                        {distance !== null && <p>📍 {distance.toFixed(1)} miles away</p>}

                        <div className="status-badge">
                          {job.status === "completed" && "✅ Completed"}
                          {job.status === "in_progress" && "🚜 In Progress"}
                          {(!job.status || job.status === "open") && "🟢 Open"}
                        </div>
                      </div>

                      {!job.isDayJob &&
                        Array.isArray(job.fields) &&
                        job.fields.length > 0 && (
                          <div className="field-list">
                            {job.fields.map((field, index) => (
                              <div
                                key={field.id || `${job.id}-field-${index}`}
                                className="field-card"
                                onClick={() =>
                                  navigate("/map", {
                                    state: { focusJobId: job.id },
                                  })
                                }
                                style={{ cursor: "pointer" }}
                              >
                                <div className="field-card-header">
                                  <strong className="field-card-title">
                                    Field #{index + 1}
                                  </strong>

                                  <span className="acre-badge">
                                    {Number(field.acres || 0).toFixed(2)} acres
                                  </span>
                                </div>

                                <div className="field-card-meta">
                                  {field.points?.length || field.polygonPath?.length || 0}{" "}
                                  boundary points
                                  {field.category ? ` • ${field.category}` : ""}
                                  {field.city || field.state
                                    ? ` • ${field.city || ""}${
                                        field.city && field.state ? ", " : ""
                                      }${field.state || ""}`
                                    : ""}
                                </div>


                                {(field.entrance?.lat || field.entranceLat) &&
  (field.entrance?.lng || field.entranceLng) && (
    <div className="field-entrance-preview">
      <strong>🚪 Entrance marked</strong>

      {(field.entrance?.note || field.entranceNote) && (
        <span>{field.entrance?.note || field.entranceNote}</span>
      )}

      <button
        type="button"
        className="btn-soft"
        onClick={(e) => {
          e.stopPropagation();

          const lat = field.entrance?.lat || field.entranceLat;
          const lng = field.entrance?.lng || field.entranceLng;

          window.open(
            `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`,
            "_blank"
          );
        }}
      >
        📍 Directions to Entrance
      </button>
    </div>
  )}
                              </div>
                            ))}
                          </div>
                        )}

                      {!job.isDayJob && job.ownerId === currentUser?.uid && (() => {
                        const allBids = bidsMap[job.id] || [];

                        const displayBids = allBids
                          .filter((bid) => {
                            const isWinningBid =
                              bid.id === job.acceptedBidId || bid.status === "accepted";

                            if ((job.status || "open") === "open") {
                              return bid.status !== "rejected";
                            }

                            return isWinningBid;
                          })
                          .sort((a, b) => a.pricePerAcre - b.pricePerAcre);

                        if (displayBids.length === 0) return null;

                        return (
                          <div className="summary-box" style={{ marginTop: "12px" }}>
                            <strong>Bids</strong>

                            {displayBids.map((bid, index) => {
                              const isWinningBid =
                                bid.id === job.acceptedBidId ||
                                bid.status === "accepted";

                              return (
                                <div
                                  key={bid.id}
                                  className="field-card"
                                  style={{ marginTop: "8px" }}
                                >
                                  {index === 0 &&
                                    !isWinningBid &&
                                    ["pending", "open"].includes(
                                      bid.status || "pending"
                                    ) && (
                                      <div
                                        style={{
                                          fontWeight: 700,
                                          color: "#16a34a",
                                        }}
                                      >
                                        🏆 Best Bid
                                      </div>
                                    )}

                                  {isWinningBid && (
                                    <div className="accepted-bid">
                                      ✅ Accepted Bid
                                    </div>
                                  )}

                                  <div className="bidder-info">
                                     <button
                                       type="button"
                                       className="link-button bidder-profile-link"
                                      onClick={() =>
                                        navigate(
                                          getProfilePath({
                                            id: bid.bidderId,
                                            uid: bid.bidderId,
                                            ...(usersMap[bid.bidderId] || {}),
                                          })
                                        )
                                      }
                                      >
                                        {bid.bidderName || "View Bidder"}
                                     </button>

                                     {(() => {
  const bidderProfile = usersMap[bid.bidderId] || {};

  const bidderRating = Number(
    bid.bidderRating ?? bidderProfile.rating ?? 0
  );

  const bidderReviewCount = Number(
    bid.bidderReviewCount ?? bidderProfile.reviewCount ?? 0
  );

  return bidderReviewCount > 0 ? (
    <div className="bidder-mini-rating">
      ⭐ {bidderRating.toFixed(1)} ({bidderReviewCount} review
      {bidderReviewCount === 1 ? "" : "s"})
    </div>
  ) : (
    <div className="bidder-mini-rating no-reviews">
      No reviews yet
    </div>
  );
})()}
                                  </div>

                                  <p>
                                    ${bid.pricePerAcre}/acre • Total: ${bid.totalBid}
                                  </p>

                                  {bid.note && <p>{bid.note}</p>}

                                  {(job.status || "open") === "open" &&
                                    ["pending", "open"].includes(
                                      bid.status || "pending"
                                    ) && (
                                      <div className="field-card-actions">
                                        <button
                                          className="btn-primary"
                                          onClick={() => acceptBid(job, bid)}
                                        >
                                          Accept Bid
                                        </button>

                                        <button
                                          className="btn-danger-soft"
                                          onClick={() => rejectBid(job, bid)}
                                        >
                                          Reject
                                        </button>
                                      </div>
                                    )}
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}

                      

                      <div className="job-card-actions">
                        {job.ownerId !== currentUser?.uid && (
                        <button 
                        className="btn-soft"
                        type="button"
                        onClick={() => openChatForJob(job)}
                        >
                          💬 Message
                          </button>
                          )}

                        <button
                          className="btn-soft"
                          onClick={() =>
                            navigate("/map", {
                              state: job.isDayJob
                                ? { focusItemId: job.id, focusItemType: "dayJob" }
                                : { focusJobId: job.id },
                            })
                          }
                        >
                          📍 View Map
                        </button>

                        {job.ownerId !== currentUser?.uid && (
  <ReportButton
    type={job.isDayJob ? "dayJob" : "fieldJob"}
    targetId={job.id}
    targetOwnerId={job.ownerId || null}
    targetTitle={
      job.isDayJob
        ? job.title || "Day Job"
        : `${getTotalAcres(job).toFixed(2)} acres • ${
            job.grassType || job.fields?.[0]?.category || "Field Job"
          }`
    }
    buttonLabel="Report"
    compact
  />
)}

                        {job.isDayJob &&
                          job.status === "open" &&
                          job.ownerId !== currentUser?.uid && (
                            <button onClick={() => applyForDayJob(job)}>Apply for Job</button>
                          )}
                               
                            {job.isDayJob && job.ownerId === currentUser?.uid && (() => {
  const apps = applicationsMap[job.id] || [];

  const displayApps = apps.filter((app) => {
    const isAccepted =
      app.id === job.acceptedApplicationId || app.status === "accepted";

    if ((job.status || "open") === "open") {
      return app.status !== "rejected";
    }

    return isAccepted;
  });

  if (displayApps.length === 0) return null;

  return (
    <div className="summary-box" style={{ marginTop: "12px" }}>
      <strong>Applications</strong>

      {displayApps.map((app) => {
        const isAccepted =
          app.id === job.acceptedApplicationId || app.status === "accepted";

        return (
          <div key={app.id} className="field-card" style={{ marginTop: "8px" }}>
            {isAccepted && <div className="accepted-bid">✅ Accepted Applicant</div>}

            <p>
              <strong>{app.applicantName || "Applicant"}</strong>
            </p>

            {typeof app.applicantRating === "number" && app.applicantRating > 0 && (
              <p
 className="review-link"
 onClick={() =>
  navigate(getProfilePath({ id: app.applicantId, uid: app.applicantId }), {
    state: { scrollToReviews: true },
  })
}
  title="View profile and reviews"
>
  ⭐ {Number(app.applicantRating || 0).toFixed(1)}
  {app.applicantReviewCount
    ? ` (${app.applicantReviewCount} reviews)`
    : " (no reviews yet)"}
</p>
            )}

            {app.note && <p>{app.note}</p>}

            {(job.status || "open") === "open" &&
              ["pending", "open"].includes(app.status || "pending") && (
                <div className="field-card-actions">
                  <button
                    className="btn-primary"
                    onClick={() => acceptDayJobApplication(job, app)}
                  >
                    Accept Applicant
                  </button>

                  <button
                    className="btn-danger-soft"
                    onClick={() => rejectDayJobApplication(job, app)}
                  >
                    Decline
                  </button>
                </div>
              )}
          </div>
        );
      })}
    </div>
  );
})()}    
                                
                        {!job.isDayJob &&
                          job.status === "open" &&
                          job.ownerId !== currentUser?.uid && (
                            <button
                              className="btn-primary"
                              onClick={() => submitBid(job)}
                            >
                              Place Bid
                            </button>
                          )}

                        {job.status === "in_progress" &&
                          (job.ownerId === currentUser?.uid ||
                            job.acceptedBy === currentUser?.uid) && (
                            <button
                              className="btn-primary"
                              onClick={() => completeJob(job)}
                            >
                              Mark Completed
                            </button>
                            )}
                            
                          

{currentUserCanReviewJob(job) && !hasReviewedJob(job) && (
  <button
    type="button"
    className="btn-primary"
    onClick={() => openReviewModal(job)}
  >
    ⭐ Review {currentUser?.uid === job.ownerId ? "Worker" : "Owner"}
  </button>
)}

{currentUserCanReviewJob(job) && hasReviewedJob(job) && (
  <button type="button" className="btn-soft" disabled>
    ✅ You Reviewed
  </button>
)}

                        {job.status === "open" && job.ownerId === currentUser?.uid && (
                          <button
                            className="btn-danger-soft"
                            onClick={async () => {
                              const confirmDelete = window.confirm(
                                "Delete this job? This cannot be undone."
                              );
                              if (!confirmDelete) return;

                              await updateDoc(doc(db, getJobCollection(job), job.id), {
                                isArchived: true,
                              });

                              setSelectedJob(null);
                            }}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}

      {selectedProduct &&
        typeof document !== "undefined" &&
        createPortal(
  <div
    className="modal market-product-modal-overlay"
    onClick={() => setSelectedProduct(null)}
    role="dialog"
    aria-modal="true"
    aria-label={`${selectedProduct.name || "Product"} details`}
  >
    <div
      className="product-detail-modal market-product-detail-modal"
      onClick={(e) => e.stopPropagation()}
    >
      {selectedProduct.image && (
        <img
          src={selectedProduct.image}
          alt={selectedProduct.name || "Product"}
          className="product-detail-img"
        />
      )}

      <div className="market-product-modal-header">
        <h2>{selectedProduct.name || "Farm Product"}</h2>

        <p className="price">
          {formatProductPrice(selectedProduct)}
        </p>

        <div className="exchange-badge-row">
          {getExchangeBadges(selectedProduct).map((badge) => (
            <span className="exchange-badge" key={badge}>
              {badge}
            </span>
          ))}
        </div>

        <div className="availability-detail-strip">
          {(() => {
            const status = getProductAvailabilityStatus(selectedProduct);
            return (
              <>
                <span className={`availability-badge availability-${status}`}>
                  {getProductAvailabilityLabel(status)}
                </span>
                {status === PRODUCT_AVAILABILITY.COMING_SOON && selectedProduct.availabilityStart && (
                  <span>Expected {selectedProduct.availabilityStart}</span>
                )}
                {status === PRODUCT_AVAILABILITY.COMING_SOON && selectedProduct.availabilityNote && (
                  <span>{selectedProduct.availabilityNote}</span>
                )}
              </>
            );
          })()}
        </div>
      </div>

{(selectedProduct.marketplaceSection || MARKETPLACE_SECTIONS.GENERAL) ===
  MARKETPLACE_SECTIONS.PLANTS_GARDEN && (
  <div className="plant-market-details plant-modal-details">
    <span>{getPlantCategoryLabel(selectedProduct.plantCategory)}</span>
    {selectedProduct.variety && <span>Variety: {selectedProduct.variety}</span>}
    {selectedProduct.potSize && <span>{selectedProduct.potSize}</span>}
    {selectedProduct.plantSize && <span>{selectedProduct.plantSize}</span>}
    {selectedProduct.annualOrPerennial && (
      <span>{getPlantLifeCycleLabel(selectedProduct.annualOrPerennial)}</span>
    )}
    {selectedProduct.sunNeeds && (
      <span>Sun: {getSunNeedsLabel(selectedProduct.sunNeeds)}</span>
    )}
    {selectedProduct.pickupLocation && (
      <span>Pickup: {selectedProduct.pickupLocation}</span>
    )}
    {selectedProduct.deliveryAvailable && <span>Delivery available</span>}
    {(selectedProduct.availabilityStart || selectedProduct.availabilityEnd) && (
      <span>
        Available{" "}
        {[selectedProduct.availabilityStart, selectedProduct.availabilityEnd]
          .filter(Boolean)
          .join(" to ")}
      </span>
    )}
  </div>
)}

{selectedProduct.productType === "hay_straw" &&
  selectedProduct.hayDetails && (
    <div className="hay-market-details hay-modal-details">
      <span>
        🌾{" "}
        {selectedProduct.hayDetails.hayType?.replace("_", " ") || "Hay"}
      </span>

      <span>
        {selectedProduct.hayDetails.baleType?.replace("_", " ") || "Bale"}
      </span>

      {selectedProduct.hayDetails.cutting && (
        <span>{selectedProduct.hayDetails.cutting}</span>
      )}

      {selectedProduct.hayDetails.baleWeight && (
        <span>{selectedProduct.hayDetails.baleWeight}</span>
      )}

      {selectedProduct.hayDetails.storedInside && (
        <span>Stored inside</span>
      )}

      {selectedProduct.hayDetails.deliveryAvailable && (
  <span>
    Delivery within{" "}
    {Number(selectedProduct.hayDetails.deliveryRadiusMiles || 0) || "?"} miles
  </span>
)}
    </div>
  )}

{selectedProduct && (
  <div className="product-market-rating product-modal-rating">
    {(() => {
      const reviewStats = getProductReviewStats(selectedProduct.id);

      return reviewStats.count > 0 ? (
        <>
          ⭐ <strong>{reviewStats.average}</strong>{" "}
          <span>
            ({reviewStats.count} review{reviewStats.count === 1 ? "" : "s"})
          </span>
        </>
      ) : (
        <span>New listing</span>
      );
    })()}
  </div>
)}

  {getReviewsForProduct(selectedProduct.id).length > 0 ? (
  <div className="product-modal-reviews">
    <h3>⭐ Product Reviews</h3>

    {getReviewsForProduct(selectedProduct.id).map((review) => (
      <div key={review.id} className="product-modal-review-card">
        <div className="product-modal-review-top">
          <strong>⭐ {review.rating}</strong>
          <span>by {review.buyerName || "Buyer"}</span>
        </div>

        <p>{review.comment}</p>

        {(review.quantity || review.productUnit) && (
          <small>
            Bought {Number(review.quantity || 1)}{" "}
            {review.productUnit || "item"}
            {Number(review.quantity || 1) === 1 ? "" : "s"}
          </small>
        )}
      </div>
    ))}
  </div>
) : (
  <div className="product-modal-reviews">
    <h3>⭐ Product Reviews</h3>
    <p className="profile-card-note">
      No reviews yet. Be the first verified buyer to leave one.
    </p>
  </div>
)}

  <div className="product-request-quantity-box">
  <label className="form-label">
    Quantity
  </label>

  <div className="quantity-stepper">
    <button
      type="button"
      className="btn-soft"
      onClick={() => setRequestQuantity((prev) => Math.max(1, prev - 1))}
    >
      -
    </button>

    <input
      type="number"
      min="1"
      max={Number(
        selectedProduct.quantityAvailable ?? selectedProduct.quantity ?? 1
      )}
      value={requestQuantity}
      onChange={(e) => {
        const maxQty = Number(
          selectedProduct.quantityAvailable ?? selectedProduct.quantity ?? 1
        );

        const nextQty = Math.max(
          1,
          Math.min(Number(e.target.value || 1), maxQty)
        );

        setRequestQuantity(nextQty);
      }}
    />

    <button
      type="button"
      className="btn-soft"
      onClick={() => {
        const maxQty = Number(
          selectedProduct.quantityAvailable ?? selectedProduct.quantity ?? 1
        );

        setRequestQuantity((prev) => Math.min(maxQty, prev + 1));
      }}
    >
      +
    </button>
  </div>

  <p className="input-helper">
    Available:{" "}
    <strong>
      {formatAvailability(
        selectedProduct.quantityAvailable ?? selectedProduct.quantity ?? 1,
        selectedProduct.unit
      )}
    </strong>
  </p>
</div>
      
      {selectedProduct.description && (
        <p className="market-product-description">{selectedProduct.description}</p>
      )}

      {(() => {
        const barter = normalizeBarterFields(selectedProduct);
        return (
          <div className="trade-options-box">
            <h3>Trade Options</h3>
            <p>
              {barter.barterEnabled
                ? barter.barterOpenToAnything
                  ? "Seller is open to any fair local trade."
                  : "Seller is willing to trade."
                : "Seller listed this as cash only."}
            </p>
            {barter.barterEnabled && !barter.barterOpenToAnything && barter.barterCategories.length > 0 && (
              <div className="exchange-badge-row">
                {barter.barterCategories.map((category) => (
                  <span className="exchange-badge" key={category}>
                    {getBarterCategoryLabel(category)}
                  </span>
                ))}
              </div>
            )}
            {barter.barterEnabled && barter.barterNotes && <p>{barter.barterNotes}</p>}
          </div>
        );
      })()}



      <div className="button-group market-product-actions">
        <button
          className="btn-soft"
          type="button"
          onClick={() => setSelectedProduct(null)}
        >
          Close
        </button>

        {selectedProduct.linkedInventoryId &&
          (selectedProduct.userId || selectedProduct.ownerId || selectedProduct.sellerId) === currentUser?.uid && (
            <button
              className="btn-soft"
              type="button"
              onClick={() => {
                setSelectedProduct(null);
                navigate("/farm-records", {
                  state: {
                    initialTab: "inventory",
                    focusRecordId: selectedProduct.linkedInventoryId,
                  },
                });
              }}
            >
              View Inventory Record
            </button>
          )}

        {(selectedProduct.marketplaceSection || MARKETPLACE_SECTIONS.GENERAL) ===
          MARKETPLACE_SECTIONS.PLANTS_GARDEN &&
          isProductOwnedByCurrentUser(selectedProduct) && (
            <>
              <button
                className="btn-soft"
                type="button"
                onClick={() => {
                  const listingId = selectedProduct.id;
                  setSelectedProduct(null);
                  navigate(`/plants-garden/new?edit=${listingId}`);
                }}
              >
                Edit Listing
              </button>

              <button
                className="btn-soft danger"
                type="button"
                onClick={() => archiveOwnPlantListing(selectedProduct)}
              >
                Delete Listing
              </button>
            </>
          )}


        

        <button
          className="btn-primary"
          type="button"
          onClick={async () => {
            if (!currentUser?.uid) {
              trackUsageEvent("message_seller_intent", { feature: "marketplace", action: "intent", relatedId: selectedProduct.id });
              trackUsageEvent("auth_gate_open", { feature: "marketplace", action: "message_seller_intent", relatedId: selectedProduct.id });
            }
            if (!requireSignIn("Create a free account or sign in to message this member.")) {
              return;
            }

            const sellerId =
              selectedProduct.userId ||
              selectedProduct.ownerId ||
              selectedProduct.sellerId;

            if (!sellerId || sellerId === currentUser?.uid) return;

            const conversationId = await findOrCreateConversation({
              ownerId: sellerId,
              isDayJob: false,
              id: selectedProduct.id,
              grassType: selectedProduct.name || "Product",
              fields: [],
            }, { skipAuthGate: true });

            setSelectedJob({
              ownerId: sellerId,
              isDayJob: false,
              fields: [],
              grassType: selectedProduct.name || "Product",
            });

            setActiveConversationId(conversationId);
            setChatInitialMessage(getTradeMessageDraft(selectedProduct));
            setSelectedProduct(null);
          }}
        >
          💬 Message Seller
        </button>

        <button
  className="btn-primary"
  type="button"
  disabled={
    getProductAvailabilityStatus(selectedProduct) === PRODUCT_AVAILABILITY.COMING_SOON ||
    getProductAvailabilityStatus(selectedProduct) === PRODUCT_AVAILABILITY.OUT_OF_STOCK ||
    selectedProduct.status === "out_of_stock" ||
    selectedProduct.status === "sold" ||
    Number(selectedProduct.quantityAvailable ?? selectedProduct.quantity ?? 1) <= 0
  }
  onClick={() => requestToBuyProduct(selectedProduct, requestQuantity)}
>
  {getProductAvailabilityStatus(selectedProduct) === PRODUCT_AVAILABILITY.COMING_SOON
    ? "Not Available Yet"
    : selectedProduct.status === "out_of_stock" ||
  Number(selectedProduct.quantityAvailable ?? selectedProduct.quantity ?? 1) <= 0
    ? "🚫 Out of Stock"
    : "🛒 Request to Buy"}
</button>

{(selectedProduct.userId ||
  selectedProduct.ownerId ||
  selectedProduct.sellerId) !== currentUser?.uid && (
  <ReportButton
    type="product"
    targetId={selectedProduct.id}
    targetOwnerId={
      selectedProduct.userId ||
      selectedProduct.ownerId ||
      selectedProduct.sellerId ||
      null
    }
    targetTitle={selectedProduct.name || "Farm Product"}
    buttonLabel="Report Product"
    compact
  />
)}
        
      </div>
    </div>
  </div>,
  document.body
)}

{selectedEvent && (
  <div className="modal" onClick={() => setSelectedEvent(null)}>
    <div
      className="product-detail-modal event-detail-modal"
      onClick={(e) => e.stopPropagation()}
    >
      {selectedEvent.image && (
        <img
          src={selectedEvent.image}
          className="product-detail-img event-detail-image"
          alt={selectedEvent.title}
          onError={(event) => {
            event.currentTarget.style.display = "none";
          }}
        />
      )}

      <h2>{selectedEvent.title || "Community Event"}</h2>

      {selectedEvent.eventMode === "curated" && (
        <div className="event-public-attribution">
          <span className="event-public-attribution-label">Public Event</span>
          {selectedEvent.organizerName && (
            <span className="event-public-attribution-organizer">
              Organizer: {selectedEvent.organizerName}
            </span>
          )}
          {selectedEvent.sourceUrl && (
            <a
              className="event-public-attribution-link"
              href={selectedEvent.sourceUrl}
              target="_blank"
              rel="noreferrer"
            >
              Official Details
            </a>
          )}
        </div>
      )}

      <p>📅 {formatEventDateRange(selectedEvent)}</p>
      {formatEventTimeRange(selectedEvent) && (
        <p>🕒 {formatEventTimeRange(selectedEvent)}</p>
      )}
      <p>📍 {getEventLocationText(selectedEvent)}</p>

      {(shouldShowEventRegistrationStatus(selectedEvent) ||
        selectedEvent.detailsComingSoon === true ||
        selectedEvent.detailsNote) && (
      <div className="availability-detail-strip event-registration-strip">
        {selectedEvent.detailsComingSoon === true && (
          <span className="availability-badge availability-coming_soon">
            Details Coming Soon
          </span>
        )}
        {shouldShowEventRegistrationStatus(selectedEvent) && (
          <span
            className={`availability-badge availability-${
              selectedEvent.registrationStatus || "open"
            }`}
          >
            {getEventRegistrationLabel(selectedEvent.registrationStatus || "open")}
          </span>
        )}
        {(selectedEvent.registrationStatus || "open") === "opens_later" &&
          selectedEvent.registrationOpensAt && (
            <span>
              Opens{" "}
              {new Date(`${selectedEvent.registrationOpensAt}T00:00:00`).toLocaleDateString()}
            </span>
          )}
        {selectedEvent.detailsNote && <span>{selectedEvent.detailsNote}</span>}
      </div>
      )}

      {selectedEvent.description && (
        <p style={{ marginTop: "10px" }}>
          {selectedEvent.description}
        </p>
      )}

      {isEventRegistrationOpen(selectedEvent) && (
  <div className="summary-box" style={{ marginTop: "14px" }}>
    <strong>🎪 Vendor Booth Availability</strong>

    


    {(selectedEvent.boothCategories || []).map((cat) => {
      const remaining =
        Number(cat.spotsAvailable || 0) -
        Number(cat.spotsReserved || 0);

      return (
        <div
          key={cat.id}
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: "6px",
          }}
        >
          <span>{cat.label}</span>
          <span
            style={{
              fontWeight: 600,
              color: remaining > 0 ? "#16a34a" : "#dc2626",
            }}
          >
            {remaining} / {cat.spotsAvailable}
          </span>
        </div>
      );
    })}
  </div>
)}

      <div className="button-group">

  <button
    className="btn-soft"
    onClick={() => setSelectedEvent(null)}
  >
    Close
  </button>

  <button
    className="btn-soft"
    onClick={() => {
      setSelectedEvent(null);
      navigate("/map", {
        state: {
          focusItemId: selectedEvent.id,
          focusItemType: "event",
        },
      });
    }}
  >
    📍 View Map
  </button>

  {(selectedEvent.ownerId === currentUser?.uid ||
  selectedEvent.userId === currentUser?.uid ||
  selectedEvent.hostId === currentUser?.uid) && (
  <button
    className="btn-danger-soft"
    type="button"
    onClick={() => archiveEvent(selectedEvent)}
  >
    Remove Event
  </button>
)}

  <button
    className="btn-soft"
    style={{ display: selectedEvent.eventMode === "curated" ? "none" : undefined }}
    onClick={async () => {
      const hostId = selectedEvent.ownerId;
      if (!hostId || hostId === currentUser?.uid) return;

      const conversationId = await findOrCreateConversation({
        ownerId: hostId,
        isDayJob: false,
        id: selectedEvent.id,
        grassType: selectedEvent.title,
        fields: [],
      });
      if (!conversationId) return;

      setSelectedJob({
        ownerId: hostId,
        isDayJob: false,
        fields: [],
        grassType: selectedEvent.title,
      });

      setActiveConversationId(conversationId);
      setSelectedEvent(null);
    }}
  >
    💬 Message Host
  </button>

  {/* ⭐ PRIMARY BUTTON */}
  {selectedEvent.reservationEnabled && (
    <button
      className={isEventRegistrationOpen(selectedEvent) ? "btn-primary" : "btn-soft"}
      type="button"
      disabled={!isEventRegistrationOpen(selectedEvent)}
      onClick={() => openReservationModal(selectedEvent)}
    >
      {getEventReservationButtonLabel(selectedEvent)}
    </button>
  )}

{(selectedEvent.ownerId || selectedEvent.userId || selectedEvent.hostId) !==
  currentUser?.uid && (
  <ReportButton
    type="event"
    targetId={selectedEvent.id}
    targetOwnerId={
      selectedEvent.ownerId || selectedEvent.userId || selectedEvent.hostId || null
    }
    targetTitle={selectedEvent.title || "Community Event"}
    buttonLabel="Report Event"
    compact
  />
)}

      </div>
    </div>
  </div>
)}

      {selectedJob && activeConversationId && (
        <div className="card chat-panel">
          <div className="chat-panel-header">
            <div>
              <h3 style={{ marginBottom: "4px" }}>
                Chat with {usersMap[selectedJob.ownerId]?.name || "Owner"}
              </h3>
              <p style={{ margin: 0 }}>
                {selectedJob.isDayJob
                  ? `About ${selectedJob.title || "day job"}`
                  : `About ${selectedJob.grassType || "field job"} • ${
                      Array.isArray(selectedJob.fields)
                        ? selectedJob.fields
                            .reduce(
                              (sum, field) => sum + (Number(field.acres) || 0),
                              0
                            )
                            .toFixed(2)
                        : "0.00"
                    } acres`}
              </p>
            </div>

            <button
              type="button"
              className="btn-soft"
              onClick={() => {
                setSelectedJob(null);
                setActiveConversationId(null);
                setChatInitialMessage("");
              }}
            >
              Close Chat
            </button>
          </div>

          <Chat
            conversationId={activeConversationId}
            currentUser={currentUser}
            userProfile={userProfile}
            initialMessage={chatInitialMessage}
          />
        </div>
      )}

      {reviewTarget && (
  <div className="modal" onClick={() => setReviewTarget(null)}>
    <div
      className="product-detail-modal review-modal"
      onClick={(e) => e.stopPropagation()}
    >
      <h2>⭐ Leave Review</h2>

      <p>
        Reviewing <strong>{reviewTarget.revieweeName}</strong>
      </p>

      <p className="profile-card-note">
        Job: {getJobReviewTitle(reviewTarget.job)}
      </p>

      <label className="form-label">Rating</label>
      <select
        value={reviewRating}
        onChange={(e) => setReviewRating(Number(e.target.value))}
      >
        <option value={5}>⭐⭐⭐⭐⭐ 5 - Excellent</option>
        <option value={4}>⭐⭐⭐⭐ 4 - Good</option>
        <option value={3}>⭐⭐⭐ 3 - Okay</option>
        <option value={2}>⭐⭐ 2 - Poor</option>
        <option value={1}>⭐ 1 - Bad</option>
      </select>

      <label className="form-label">Comment</label>
      <textarea
        value={reviewComment}
        onChange={(e) => setReviewComment(e.target.value)}
        rows={4}
        placeholder="Example: Showed up on time, communicated well, and did great work."
      />

      <div className="button-group">
        <button
          type="button"
          className="btn-soft"
          onClick={() => setReviewTarget(null)}
        >
          Cancel
        </button>

        <button
          type="button"
          className="btn-primary"
          disabled={submittingReview}
          onClick={submitJobReview}
        >
          {submittingReview ? "Submitting..." : "Submit Review"}
        </button>
      </div>
    </div>
  </div>
)}
      <AuthActionGate {...authGateProps} />
    </div>
  );
};

export default JobFeed;
